import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Box, Typography, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
  Paper, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Checkbox, LinearProgress, TextField, Collapse, Alert, MenuItem
} from "@mui/material";
import DashboardCards from "../components/DashboardCards";
import InvoiceForm from "../components/InvoiceForm";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PrintIcon from "@mui/icons-material/Print";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import QRCode from "qrcode";
import dayjs from "dayjs";
import { useReactToPrint } from "react-to-print";

import {
  cancelInvoiceApprovalApi,
  checkSubmittedInvoicesApi,
  dashboard,
  getFbrInvoiceTypes,
  getInvoiceApprovalsApi,
  getInvoiceDesignApi,
  markInvoiceApprovalSubmittedApi,
  processInvoiceApi,
  saveInvoiceApprovalApi,
  verifyAdminApproval,
} from "../api";
import { useData } from "../ContextApi/ContextProvider";
import { QRCodeCanvas } from "qrcode.react";
import TableToolbar from "./TableToolbar";
import fbrLogo from "../assets/fbr.png";
import {
  DESIGN_CANVAS_WIDTH,
  DEFAULT_BREAKDOWN_ROWS,
  DEFAULT_ITEM_COLUMNS,
  INVOICE_ITEM_FIELD_OPTIONS,
  getDesignBlock,
  loadLocalInvoiceDesign,
  saveLocalInvoiceDesign,
  toPrintMm,
} from "../utils/invoiceDesign";

/* ------------------ Schema ------------------ */
const summaryFields = [
  "invoiceType", "invoiceDate", "sellerNTNCNIC", "sellerBusinessName", "sellerProvince", "sellerAddress",
  "buyerNTNCNIC", "buyerSalesTaxNo", "buyerBusinessName", "buyerProvince", "buyerAddress", "buyerRegistrationType",
  "termsOfSale", "invoiceRefNo", "invoiceNumber","scenarioId","reason"
];
const itemFields = [
  "hsCode", "productDescription", "rate", "uoM", "quantity", "totalValues", "valueSalesExcludingST",
  "fixedNotifiedValueOrRetailPrice", "salesTaxApplicable", "salesTaxWithheldAtSource", "extraTax", "furtherTax",
  "sroScheduleNo", "fedPayable", "discount", "saleType", "sroItemSerialNo","validation","remarks","invoiceNumber"
];

const FIELD_LABELS = {
  invoiceType: "Invoice Type",
  invoiceDate: "Invoice Date",
  sellerNTNCNIC: "Seller NTN/CNIC",
  sellerBusinessName: "Seller Business Name",
  sellerProvince: "Seller Province",
  sellerAddress: "Seller Address",
  buyerNTNCNIC: "Buyer NTN/CNIC",
  buyerSalesTaxNo: "Buyer Sales Tax No",
  buyerBusinessName: "Buyer Business Name",
  buyerProvince: "Buyer Province",
  buyerAddress: "Buyer Address",
  buyerRegistrationType: "Buyer Registration Type",
  termsOfSale: "Terms of Sale",
  invoiceRefNo: "Invoice Ref No",
  invoiceNumber: "Invoice Number",
  scenarioId: "Scenario ID",
  reason: "Reason",
  hsCode: "HS Code",
  productDescription: "Product Description",
  rate: "Rate",
  uoM: "UoM",
  quantity: "Quantity",
  totalValues: "Total Values",
  valueSalesExcludingST: "Value Sales Excluding ST",
  fixedNotifiedValueOrRetailPrice: "Fixed Notified Value/Retail Price",
  salesTaxApplicable: "Sales Tax Applicable",
  salesTaxWithheldAtSource: "Sales Tax Withheld At Source",
  extraTax: "Extra Tax",
  furtherTax: "Further Tax",
  sroScheduleNo: "SRO Schedule No",
  fedPayable: "FED Payable",
  discount: "Discount",
  saleType: "Sale Type",
  sroItemSerialNo: "SRO Item Serial No",
  validation: "Validation",
  remarks: "Remarks",
  fbrinvoicenumber: "FBR Invoice Number",
  FBRqrcode: "FBR QR Code",
};

const FIELD_ALIASES = {
  sellerNTNCNIC: ["Seller NTN CNIC", "Seller NTN/CNIC", "Seller NTN", "Seller CNIC"],
  buyerNTNCNIC: ["Buyer NTN CNIC", "Buyer NTN/CNIC", "Buyer NTN", "Buyer CNIC"],
  buyerSalesTaxNo: ["Buyer Sales Tax No", "Buyer GST No", "Sales Tax No", "GST No", "buyer_sales_tax_no"],
  termsOfSale: ["Terms of Sale", "Terms of Sales", "Term of Sale", "Term of Sales", "terms_of_sale"],
  uoM: ["UOM", "UoM", "Unit", "Unit of Measure"],
  scenarioId: ["Scenario ID", "Scenario"],
  hsCode: ["HS Code", "HSCode"],
  sroScheduleNo: ["SRO Schedule No", "SRO No", "SRO"],
  sroItemSerialNo: ["SRO Item Serial No", "Serial No", "SRO Serial No"],
};

const NUMERIC_FIELDS = new Set([
  "quantity",
  "totalValues",
  "valueSalesExcludingST",
  "fixedNotifiedValueOrRetailPrice",
  "salesTaxApplicable",
  "salesTaxWithheldAtSource",
  "extraTax",
  "furtherTax",
  "fedPayable",
  "discount",
]);

/* Required keys we validate for */
const REQUIRED_SUMMARY = ["invoiceNumber"];
const REQUIRED_ITEM = ["productDescription"]; // add "quantity","rate" etc. if you want stricter checks
const EXCEL_EXPORT_ITEM_FIELDS = itemFields.filter((field) => field !== "invoiceNumber");

const FBR_REQUEST_TIMEOUT_MS = 60000;
const FBR_BATCH_SIZE = 1;
const PDF_UPLOAD_MAX_SIZE = 20 * 1024 * 1024;
const DEFAULT_PDF_PRODUCT_DESCRIPTION = "Imported PDF invoice line";

/* ------------------ Helpers ------------------ */
const getFbrErrorMessage = (validationResponse = {}) => {
  const errorCode = String(validationResponse?.errorCode || "").trim();
  const error = String(validationResponse?.error || "").trim();

  if (errorCode === "0510" || error.toLowerCase() === "execption occured" || error.toLowerCase() === "exception occured") {
    return "FBR system not responding";
  }

  return error;
};

const isErrorStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return ["invalid", "error", "failed", "failure"].includes(normalized);
};

const hasErrorText = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return false;

  return [
    "error",
    "failed",
    "invalid",
    "timeout",
    "exception",
    "not responding",
    "did not respond",
  ].some((needle) => normalized.includes(needle));
};

const hasItemError = (item = {}) =>
  isErrorStatus(item.validation) || hasErrorText(item.remarks);

const hasInvoiceError = (invoice = {}) => {
  if (invoice.alreadySubmitted) return false;

  return (
    isErrorStatus(invoice.validation) ||
    invoice.fbrinvoicenumber === "ERROR" ||
    hasErrorText(invoice.remarks) ||
    (Array.isArray(invoice.items) && invoice.items.some(hasItemError))
  );
};

/** Canonicalize a header: lowercase + remove non-alphanumerics */
const canon = (s) => String(s || "").replace(/[^a-z0-9]/gi, "").toLowerCase();

const stripEdgeNoise = (value) =>
  String(value ?? "")
    .trim()
    .replace(/^[\s'",“”‘’`´,]+|[\s'",“”‘’`´,]+$/g, "")
    .trim();

const normalizeExcelValue = (field, value) => {
  if (value == null) return "";
  const cleaned = stripEdgeNoise(value);
  if (NUMERIC_FIELDS.has(field)) {
    return cleaned.replace(/,/g, "");
  }
  return cleaned;
};

const parseExcelNumber = (value) =>
  Number.parseFloat(String(value ?? "").replace(/,/g, ""));

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const isPresent = (value) => {
  const normalizedValue = String(value ?? "").trim();
  return normalizedValue && !/^(null|undefined)$/i.test(normalizedValue);
};

const toNumber = (value) => {
  const parsed = parseExcelNumber(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatWhole = (value) =>
  Math.trunc(Math.abs(value)).toLocaleString();

const formatPaisa = (value) =>
  Math.round((Math.abs(value) % 1) * 100).toString().padStart(2, "0");

const numberToWords = (value) => {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const convertLessThanThousand = (num) => {
    if (num < 20) return ones[num];
    if (num < 100) return `${tens[Math.floor(num / 10)]}${num % 10 ? ` ${ones[num % 10]}` : ""}`;
    return `${ones[Math.floor(num / 100)]} Hundred${num % 100 ? ` ${convertLessThanThousand(num % 100)}` : ""}`;
  };

  const rounded = Math.trunc(Math.abs(Number(value) || 0));
  if (!rounded) return "Zero";

  const units = [
    { value: 1000000000, label: "Billion" },
    { value: 1000000, label: "Million" },
    { value: 1000, label: "Thousand" },
    { value: 1, label: "" },
  ];

  let remaining = rounded;
  const parts = [];
  units.forEach((unit) => {
    const count = Math.floor(remaining / unit.value);
    if (count) {
      parts.push(`${convertLessThanThousand(count)}${unit.label ? ` ${unit.label}` : ""}`);
      remaining %= unit.value;
    }
  });

  return parts.join(" ");
};

const amountToWords = (value) => {
  const amount = Math.abs(Number(value) || 0);
  const rupees = Math.trunc(amount);
  const paisa = Math.round((amount - rupees) * 100);
  return `${numberToWords(rupees)} Rupees${paisa ? ` and ${numberToWords(paisa)} Paisa` : ""}`;
};

const getFieldLabel = (field) => FIELD_LABELS[field] || field;

const getScenarioId = (row) => row?.scenario_id || row?.scenarioId || row?.id || "";
const getHsCode = (row) => row?.hs_code || row?.hsCode || row?.hS_CODE || row?.HS_CODE || "";
const getHsUom = (row) => row?.uom || row?.uoM || row?.UOM || "";
const getHsDescription = (row) => row?.description || row?.desc || row?.hs_desc || row?.hsDesc || "";
const getHsSaleType = (row) => row?.sale_type || row?.saleType || row?.transaction_desc || row?.transactionDesc || "";
const getHsRate = (row) => row?.rate || row?.rate_desc || row?.rateDesc || "";
const getHsSroSchedule = (row) => row?.sro_schedule_no || row?.sroScheduleNo || row?.sro_schedule || row?.sroSchedule || "";
const getHsSroItem = (row) => row?.sro_item_no || row?.sroItemNo || row?.sroItemSerialNo || "";

const normalizeLookupText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const findProvinceDesc = (value, fallback = "") => {
  const normalized = normalizeLookupText(value);
  const known = [
    "PUNJAB",
    "SINDH",
    "KHYBER PAKHTUNKHWA",
    "BALOCHISTAN",
    "ISLAMABAD CAPITAL TERRITORY",
    "AZAD JAMMU AND KASHMIR",
    "GILGIT BALTISTAN",
  ];
  const matched = known.find((province) => normalizeLookupText(province) === normalized);
  return matched || fallback || "Punjab";
};

const parsePdfAmount = (value) => {
  const parsed = Number.parseFloat(String(value || "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const findPdfValue = (text, patterns) => {
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return stripEdgeNoise(match[1]).replace(/\s{2,}/g, " ");
  }
  return "";
};

const findPdfDate = (text) => {
  const value = findPdfValue(text, [
    /(?:invoice|bill|document)?\s*date\s*[:#-]?\s*([0-3]?\d[./-][01]?\d[./-]\d{2,4})/i,
    /(?:invoice|bill|document)?\s*date\s*[:#-]?\s*([0-3]?\d[\s-][A-Za-z]{3,9}[\s-]\d{2,4})/i,
    /(?:date)\s*[:#-]?\s*(\d{4}[./-][01]?\d[./-][0-3]?\d)/i,
  ]);
  if (!value) return dayjs().format("YYYY-MM-DD");

  const normalized = value.replace(/[.]/g, "/");
  const ymd = normalized.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (ymd) {
    return `${ymd[1]}-${ymd[2].padStart(2, "0")}-${ymd[3].padStart(2, "0")}`;
  }

  const dmy = normalized.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (dmy) {
    const year = dmy[3].length === 2 ? `20${dmy[3]}` : dmy[3];
    return `${year}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  }

  const monthNames = {
    jan: "01",
    january: "01",
    feb: "02",
    february: "02",
    mar: "03",
    march: "03",
    apr: "04",
    april: "04",
    may: "05",
    jun: "06",
    june: "06",
    jul: "07",
    july: "07",
    aug: "08",
    august: "08",
    sep: "09",
    sept: "09",
    september: "09",
    oct: "10",
    october: "10",
    nov: "11",
    november: "11",
    dec: "12",
    december: "12",
  };
  const dmyName = value.match(/^(\d{1,2})[\s-]([A-Za-z]{3,9})[\s-](\d{2,4})$/);
  if (dmyName) {
    const month = monthNames[dmyName[2].toLowerCase()];
    const year = dmyName[3].length === 2 ? `20${dmyName[3]}` : dmyName[3];
    if (month) return `${year}-${month}-${dmyName[1].padStart(2, "0")}`;
  }

  return dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
};

const findPdfTotal = (text) => {
  const labels = [
    "grand total",
    "net total",
    "invoice total",
    "total amount",
    "amount payable",
    "total",
  ];
  for (const label of labels) {
    const pattern = new RegExp(`${label.replace(/\s+/g, "\\s+")}\\s*(?:rs\\.?|pkr|amount)?\\s*[:#-]?\\s*([\\d,]+(?:\\.\\d{1,2})?)`, "i");
    const match = text.match(pattern);
    if (match?.[1]) return parsePdfAmount(match[1]);
  }
  const amounts = Array.from(text.matchAll(/(?:rs\.?|pkr)?\s*([\d]{1,3}(?:,\d{3})+(?:\.\d{1,2})?|\d+\.\d{2})/gi))
    .map((match) => parsePdfAmount(match[1]))
    .filter((amount) => amount > 0);
  return amounts.length ? Math.max(...amounts) : 0;
};

const parseFbrStylePdf = (text) => {
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const field = {};

  const serialLine = lines.find((line) => /Serial\s*No/i.test(line));
  if (serialLine) {
    const match = serialLine.match(/Serial\s*No\.?\s*:\s*(.*?)\s+Date\s*:\s*(.*?)\s+Time/i);
    if (match) {
      field.invoiceNumber = stripEdgeNoise(match[1]);
      field.invoiceDate = match[2] ? findPdfDate(`Date: ${stripEdgeNoise(match[2])}`) : "";
    }
  }

  const partyIndex = lines.findIndex((line) => /Supplier'?s?\s*Name/i.test(line) && /Buyer'?s?\s*Name/i.test(line));
  if (partyIndex >= 0) {
    const match = lines[partyIndex].match(/Supplier'?s?\s*Name\s*:\s*(.*?)\s+Buyer'?s?\s*Name\s*:\s*(.*)/i);
    if (match) {
      field.sellerBusinessName = stripEdgeNoise(match[1]);
      field.buyerBusinessName = stripEdgeNoise(match[2]);
      const next = lines[partyIndex + 1] || "";
      if (next && !/:/.test(next) && !/Address/i.test(next)) {
        field.buyerBusinessName = `${field.buyerBusinessName} ${next}`.trim();
      }
    }
  }

  const addressLine = lines.find((line) => /^Address\s*:/i.test(line) && /\sAddress\s*:/i.test(line.replace(/^Address\s*:/i, "")));
  if (addressLine) {
    const match = addressLine.match(/^Address\s*:\s*(.*?)\s+Address\s*:\s*(.*)$/i);
    if (match) {
      field.sellerAddress = stripEdgeNoise(match[1]);
      field.buyerAddress = stripEdgeNoise(match[2]);
    }
  }

  const salesTaxLine = lines.find((line) => /^Sales\s*Tax\s*No/i.test(line) && /\sSales\s*Tax\s*No/i.test(line.replace(/^Sales\s*Tax\s*No\.?\s*:/i, "")));
  if (salesTaxLine) {
    const match = salesTaxLine.match(/^Sales\s*Tax\s*No\.?\s*:\s*(.*?)\s+Sales\s*Tax\s*No\.?\s*:\s*(.*)$/i);
    if (match) {
      field.sellerSalesTaxNo = stripEdgeNoise(match[1]);
      field.buyerSalesTaxNo = stripEdgeNoise(match[2]);
    }
  }

  const nationalTaxLine = lines.find((line) => /^National\s*Tax\s*No/i.test(line) && /\sNational\s*Tax\s*No/i.test(line.replace(/^National\s*Tax\s*No\.?\s*:/i, "")));
  if (nationalTaxLine) {
    const match = nationalTaxLine.match(/^National\s*Tax\s*No\.?\s*:\s*(.*?)\s+National\s*Tax\s*No\.?\s*:\s*(.*)$/i);
    if (match) {
      field.sellerNTNCNIC = stripEdgeNoise(match[1]);
      field.buyerNTNCNIC = stripEdgeNoise(match[2]);
    }
  }

  const termsLine = lines.find((line) => /^Terms\s*of\s*Sales?\s*:/i.test(line));
  if (termsLine) {
    field.termsOfSale = stripEdgeNoise(termsLine.replace(/^Terms\s*of\s*Sales?\s*:/i, ""));
  }

  const totalLine = lines.find((line) => /\bTotal\b/i.test(line) && /[\d,]+\s+[\d,]+\s+[\d,]+/.test(line));
  if (totalLine) {
    const numbers = Array.from(totalLine.matchAll(/[\d,]+(?:\.\d+)?/g)).map((match) => parsePdfAmount(match[0]));
    if (numbers.length >= 3) {
      field.totalQuantity = numbers[0];
      field.valueSalesExcludingST = numbers[numbers.length - 3];
      field.salesTaxApplicable = numbers[numbers.length - 2];
      field.totalValues = numbers[numbers.length - 1];
    }
  }

  const items = [];
  const rowRegex = /^(\d+(?:\.\d+)?)\s+(?:(.*?)\s+)?([\d,]+)\s+([\d,]+)\s+##\s+(\d+(?:\.\d+)?%)\s+([\d,]+)\s+([\d,]+)$/i;
  for (let index = 0; index < lines.length; index += 1) {
    const match = lines[index].match(rowRegex);
    if (!match) continue;

    const continuation = [];
    for (let nextIndex = index + 1; nextIndex < lines.length; nextIndex += 1) {
      const next = lines[nextIndex];
      if (
        rowRegex.test(next) ||
        /\bTotal\b/i.test(next) ||
        /Tax\s*Value|Signature|Net\s*Tax|Name\s*&\s*Designation/i.test(next)
      ) {
        break;
      }
      if (
        /^(Rs\.?|Ps\.?|Qty|Description|Unit Price|Value Exclusive|Rate|Total Tax|Value Inclusive)$/i.test(next) ||
        /^(Original|Duplicate|Triplicate|SALES TAX INVOICE)$/i.test(next)
      ) {
        continue;
      }
      continuation.push(next);
    }

    items.push({
      quantity: parsePdfAmount(match[1]) || 1,
      productDescription: [match[2], ...continuation].filter(Boolean).join(" ").replace(/\s+/g, " ").trim(),
      totalValues: parsePdfAmount(match[7]),
      valueSalesExcludingST: parsePdfAmount(match[4]),
      salesTaxApplicable: parsePdfAmount(match[6]),
      fixedNotifiedValueOrRetailPrice: parsePdfAmount(match[3]),
      rate: match[5],
    });
  }

  return { ...field, items };
};

const buildPdfInvoiceDraft = ({ text, fileName, globalData, invoiceTypeOptions, scenarioOptions }) => {
  const configuredHsCode = Array.isArray(globalData?.hs_codes) ? globalData.hs_codes[0] : null;
  const structured = parseFbrStylePdf(text);
  const invoiceNumber =
    structured.invoiceNumber ||
    findPdfValue(text, [
      /(?:invoice|inv|bill)\s*(?:no|number|#)\s*[:#-]?\s*([A-Z0-9/_-]+)/i,
      /(?:serial)\s*(?:no|number|#)\s*[:#-]?\s*([A-Z0-9/_-]+)/i,
    ]) ||
    String(fileName || "pdf-invoice").replace(/\.pdf$/i, "").replace(/\s+/g, "-");
  const buyerBusinessName = findPdfValue(text, [
    /(?:buyer'?s?\s*name|customer\s*name|client\s*name|billed\s*to)\s*[:#-]?\s*([^\n\r]+)/i,
    /(?:buyer|customer|client)\s*[:#-]\s*([^\n\r]+)/i,
  ]);
  const buyerNTNCNIC = findPdfValue(text, [
    /(?:buyer\s*)?(?:ntn|cnic|national\s*tax\s*no\.?)\s*[:#-]?\s*([A-Z0-9-]+)/i,
  ]);
  const buyerSalesTaxNo = findPdfValue(text, [
    /(?:buyer\s*)?(?:sales\s*tax\s*no\.?|strn|gst\s*no\.?)\s*[:#-]?\s*([A-Z0-9-]+)/i,
  ]);
  const buyerAddress = findPdfValue(text, [
    /(?:buyer\s*)?address\s*[:#-]?\s*([^\n\r]+)/i,
    /(?:bill\s*to|billed\s*to)\s*[:#-]?\s*[^\n\r]*[\n\r]+([^\n\r]+)/i,
  ]);
  const totalValues = findPdfTotal(text);
  const salesTaxApplicable = parsePdfAmount(findPdfValue(text, [
    /(?:sales\s*tax|gst|tax)\s*[:#-]?\s*([,\d]+(?:\.\d{1,2})?)/i,
  ]));
  const valueSalesExcludingST = totalValues && salesTaxApplicable ? Math.max(totalValues - salesTaxApplicable, 0) : totalValues;
  const pdfItems = structured.items.length
    ? structured.items
    : [{
        productDescription: findPdfValue(text, [
          /(?:description|particulars|item)\s*[:#-]?\s*([^\n\r]+)/i,
        ]) || configuredHsCode?.description || DEFAULT_PDF_PRODUCT_DESCRIPTION,
        rate: configuredHsCode?.rate || (salesTaxApplicable && valueSalesExcludingST ? `${Math.round((salesTaxApplicable / valueSalesExcludingST) * 100)}%` : ""),
        quantity: 1,
        totalValues: totalValues || valueSalesExcludingST || 0,
        valueSalesExcludingST: valueSalesExcludingST || 0,
        fixedNotifiedValueOrRetailPrice: 0,
        salesTaxApplicable: salesTaxApplicable || 0,
      }];

  return {
    invoiceType: invoiceTypeOptions[0]?.docDescription || "Sale Invoice",
    invoiceDate: structured.invoiceDate || findPdfDate(text),
    sellerNTNCNIC: globalData?.ntn_cnic || structured.sellerNTNCNIC || "",
    sellerBusinessName: globalData?.company_name || globalData?.full_name || structured.sellerBusinessName || "",
    sellerProvince: findProvinceDesc(globalData?.province),
    sellerAddress: globalData?.business_address || structured.sellerAddress || globalData?.city || "",
    buyerNTNCNIC: structured.buyerNTNCNIC || buyerNTNCNIC,
    buyerSalesTaxNo: structured.buyerSalesTaxNo || buyerSalesTaxNo,
    buyerBusinessName: structured.buyerBusinessName || buyerBusinessName || "PDF Imported Buyer",
    buyerProvince: findProvinceDesc(findPdfValue(text, [/province\s*[:#-]?\s*([^\n\r]+)/i]), globalData?.province || "Punjab"),
    buyerAddress: structured.buyerAddress || buyerAddress || "PDF imported address",
    buyerRegistrationType: structured.buyerNTNCNIC || structured.buyerSalesTaxNo || buyerNTNCNIC || buyerSalesTaxNo ? "Registered" : "Unregistered",
    termsOfSale: structured.termsOfSale || findPdfValue(text, [/terms\s*of\s*sale\s*[:#-]?\s*([^\n\r]+)/i]) || "Cash",
    invoiceRefNo: invoiceNumber,
    invoiceNumber,
    scenarioId: scenarioOptions[0] || globalData?.scenarioId || "",
    reason: "Imported from PDF",
    items: pdfItems.map((item) => ({
        hsCode: configuredHsCode?.hs_code || "",
        productDescription: item.productDescription || configuredHsCode?.description || DEFAULT_PDF_PRODUCT_DESCRIPTION,
        rate: configuredHsCode?.rate || item.rate || "",
        uoM: configuredHsCode?.uom || "Number, Pieces, units",
        quantity: item.quantity || 1,
        totalValues: item.totalValues || 0,
        valueSalesExcludingST: item.valueSalesExcludingST || 0,
        fixedNotifiedValueOrRetailPrice: item.fixedNotifiedValueOrRetailPrice || 0,
        salesTaxApplicable: item.salesTaxApplicable || 0,
        salesTaxWithheldAtSource: 0,
        extraTax: "",
        furtherTax: 0,
        sroScheduleNo: configuredHsCode?.sro_schedule_no || configuredHsCode?.sroScheduleNo || "",
        fedPayable: 0,
        discount: 0,
        saleType: configuredHsCode?.sale_type || configuredHsCode?.saleType || "Goods at standard rate (default)",
        sroItemSerialNo: configuredHsCode?.sro_item_no || configuredHsCode?.sroItemNo || configuredHsCode?.sroItemSerialNo || "",
        validation: "",
        remarks: "Extracted from PDF. Please review fields if validation fails.",
        invoiceNumber: "",
      })),
    fbrinvoicenumber: "",
    FBRqrcode: "",
    validation: "",
    alreadySubmitted: false,
    disabledReason: "",
    remarks: "Extracted from PDF. Please review fields if validation fails.",
    expanded: false,
  };
};

const buildVisualPdfText = (items) => {
  const positioned = items
    .map((item) => ({
      text: String(item.str || "").trim(),
      x: item.transform?.[4] || 0,
      y: item.transform?.[5] || 0,
    }))
    .filter((item) => item.text);

  positioned.sort((a, b) => b.y - a.y || a.x - b.x);

  const lines = [];
  positioned.forEach((item) => {
    let line = lines.find((candidate) => Math.abs(candidate.y - item.y) < 4);
    if (!line) {
      line = { y: item.y, parts: [] };
      lines.push(line);
    }
    line.parts.push(item);
  });

  return lines
    .map((line) =>
      line.parts
        .sort((a, b) => a.x - b.x)
        .map((part) => part.text)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim()
    )
    .filter(Boolean)
    .join("\n");
};

const parseRatePercent = (rate) => {
  const parsed = parseExcelNumber(String(rate || "").replace("%", ""));
  return Number.isFinite(parsed) ? parsed : 18;
};

const toFriendlyExcelRows = (rows) =>
  rows.map((row) =>
    Object.keys(row).reduce((acc, field) => {
      acc[getFieldLabel(field)] = row[field];
      return acc;
    }, {})
  );

const toFriendlyExcelHeaders = (fields) => fields.map(getFieldLabel);

const buildDashboardInvoicePrintHtml = (invoice) => {
  const items = Array.isArray(invoice?.items) ? invoice.items : [];
  const totalExcl = items.reduce((sum, item) => sum + toNumber(item.valueSalesExcludingST), 0);
  const totalTax = items.reduce((sum, item) => sum + toNumber(item.salesTaxApplicable), 0);
  const totalIncl = totalExcl + totalTax;
  const firstDescriptionCode = items.find((item) => isPresent(item.sroItemSerialNo))?.sroItemSerialNo || "";
  const buyerSalesTaxNo = invoice?.buyerSalesTaxNo || invoice?.buyer_sales_tax_no || invoice?.buyerSalesTaxNumber || "";
  const termsOfSale = invoice?.termsOfSale || invoice?.terms_of_sale || "";

  const rows = items.map((item) => {
    const quantity = toNumber(item.quantity);
    const valueExcl = toNumber(item.valueSalesExcludingST);
    const tax = toNumber(item.salesTaxApplicable);
    const incl = valueExcl + tax;
    const price = quantity ? valueExcl / quantity : toNumber(item.fixedNotifiedValueOrRetailPrice || item.totalValues);

    return `
      <tr>
        <td>${escapeHtml(quantity ? quantity.toLocaleString() : item.quantity)}</td>
        <td>${escapeHtml(item.productDescription)}</td>
        <td>${escapeHtml(item.hsCode)}</td>
        <td>${escapeHtml(price ? Number(price.toFixed(3)).toLocaleString() : "")}</td>
        <td>${formatWhole(valueExcl)}</td>
        <td>${formatPaisa(valueExcl)}</td>
        <td>${escapeHtml(item.rate)}</td>
        <td>${formatWhole(tax)}</td>
        <td>${formatPaisa(tax)}</td>
        <td>${formatWhole(incl)}</td>
        <td>${formatPaisa(incl)}</td>
      </tr>
    `;
  }).join("");

  return `
    <style>
      .dashboard-invoice-print {
        width: 820px;
        padding: 24px;
        background: #fff;
        color: #000;
        font-family: "Times New Roman", serif;
        font-size: 15px;
        box-sizing: border-box;
      }
      .dashboard-invoice-title {
        display: inline-block;
        border: 1.5px solid #000;
        padding: 8px 24px;
        font-size: 20px;
        font-weight: 700;
        margin-bottom: 14px;
      }
      .dashboard-invoice-top {
        display: grid;
        grid-template-columns: 1fr 1fr 1.3fr;
        gap: 28px;
        border-bottom: 1.5px solid #000;
        padding-bottom: 5px;
        margin-bottom: 8px;
      }
      .dashboard-invoice-meta {
        display: flex;
        align-items: baseline;
        gap: 6px;
        white-space: nowrap;
      }
      .dashboard-invoice-line {
        border-bottom: 1.5px solid #000;
        display: inline-block;
        flex: 1;
        min-width: 0;
        padding: 0 8px;
      }
      .dashboard-invoice-parties {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 48px;
        line-height: 1.35;
        margin-bottom: 8px;
      }
      .dashboard-invoice-terms {
        margin-top: 6px;
      }
      .dashboard-invoice-description-code {
        text-align: right;
        margin: 4px 0 8px;
      }
      .dashboard-invoice-fbr-line {
        margin-top: 8px;
        font-weight: 700;
      }
      .dashboard-invoice-verification {
        display: grid;
        grid-template-columns: 120px 110px 1fr 220px;
        align-items: end;
        gap: 20px;
        margin-top: 34px;
      }
      .dashboard-invoice-logo {
        width: 110px;
        height: 110px;
        object-fit: contain;
      }
      .dashboard-invoice-qr-placeholder {
        width: 100px;
        height: 100px;
        border: 1.5px solid #000;
        box-sizing: border-box;
        background:
          linear-gradient(90deg, transparent 48%, #000 48%, #000 52%, transparent 52%),
          linear-gradient(0deg, transparent 48%, #000 48%, #000 52%, transparent 52%);
        opacity: 0.18;
      }
      .dashboard-invoice-print table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;
      }
      .dashboard-invoice-print th,
      .dashboard-invoice-print td {
        border: 1.5px solid #000;
        padding: 5px;
      }
      .dashboard-invoice-print th {
        text-align: center;
        font-weight: 700;
      }
      .dashboard-invoice-print td {
        text-align: right;
      }
      .dashboard-invoice-print td:nth-child(2) {
        text-align: left;
      }
      .dashboard-invoice-summary {
        display: grid;
        grid-template-columns: 1fr 110px;
        gap: 14px;
        line-height: 1.45;
      }
      .dashboard-invoice-summary div:nth-child(odd) {
        text-align: right;
      }
      .dashboard-invoice-signature {
        margin-top: 42px;
        line-height: 1.5;
        font-weight: 700;
      }
      .dashboard-invoice-signature span {
        border-bottom: 1.5px solid #000;
        display: inline-block;
        min-width: 230px;
      }
      @media print {
        @page {
          size: A4 portrait;
          margin: 12mm;
        }
        .dashboard-invoice-print {
          width: 100%;
          padding: 0;
        }
      }
    </style>
    <div class="dashboard-invoice-print">
      <div class="dashboard-invoice-title">SALES TAX INVOICE</div>
      <div class="dashboard-invoice-top">
        <div class="dashboard-invoice-meta"><b>Serial No.:</b><span class="dashboard-invoice-line">${escapeHtml(invoice?.invoiceNumber)}</span></div>
        <div class="dashboard-invoice-meta"><b>Date:</b><span class="dashboard-invoice-line">${escapeHtml(invoice?.invoiceDate)}</span></div>
        <div class="dashboard-invoice-meta"><b>Time of Supply:</b><span class="dashboard-invoice-line">&nbsp;</span></div>
      </div>
      <div class="dashboard-invoice-parties">
        <div>
          <div><b>Supplier's Name:</b> <b><u>${escapeHtml(invoice?.sellerBusinessName)}</u></b></div>
          <div><b>Address:</b> ${escapeHtml(invoice?.sellerAddress)}</div>
          <div><b>Sales Tax No.:</b> <b><u>${escapeHtml(invoice?.sellerGSTNo || invoice?.sellerGstNo || invoice?.sellerGSTNumber)}</u></b></div>
          <div><b>National Tax No.:</b> <b><u>${escapeHtml(invoice?.sellerNTNCNIC)}</u></b></div>
          <div class="dashboard-invoice-terms"><b>Terms of Sale:</b> <span class="dashboard-invoice-line" style="min-width: 310px;">${escapeHtml(termsOfSale) || "&nbsp;"}</span></div>
        </div>
        <div>
          <div><b>Buyer's Name:</b> <b><u>${escapeHtml(invoice?.buyerBusinessName)}</u></b></div>
          <div><b>Address:</b> ${escapeHtml(invoice?.buyerAddress)}</div>
          <div><b>Sales Tax No.:</b> <b><u>${escapeHtml(buyerSalesTaxNo)}</u></b></div>
          <div><b>National Tax No.:</b> <b><u>${escapeHtml(invoice?.buyerNTNCNIC)}</u></b></div>
        </div>
      </div>
      <div class="dashboard-invoice-description-code">(Description code: ${escapeHtml(firstDescriptionCode)})</div>
      <table>
        <colgroup>
          <col style="width: 7%;" />
          <col style="width: 18%;" />
          <col style="width: 10%;" />
          <col style="width: 8%;" />
          <col style="width: 10%;" />
          <col style="width: 4%;" />
          <col style="width: 8%;" />
          <col style="width: 10%;" />
          <col style="width: 4%;" />
          <col style="width: 10%;" />
          <col style="width: 4%;" />
        </colgroup>
        <thead>
          <tr>
            <th rowspan="2">Qty</th>
            <th rowspan="2">Description</th>
            <th rowspan="2">Hs Code</th>
            <th rowspan="2">Price</th>
            <th colspan="2">Value Exclusive<br />Sales Tax</th>
            <th rowspan="2">Rate of<br />Sales<br />Tax</th>
            <th colspan="2">Total Sales Tax<br />Payable</th>
            <th colspan="2">Value Inclusive<br />Sales Tax</th>
          </tr>
          <tr>
            <th>Rs.</th>
            <th>Ps.</th>
            <th>Rs.</th>
            <th>Ps.</th>
            <th>Rs.</th>
            <th>Ps.</th>
          </tr>
        </thead>
        <tbody>
          ${rows}
          <tr>
            <td colspan="4" style="text-align:right;"><b>Total</b></td>
            <td><b>${formatWhole(totalExcl)}</b></td>
            <td><b>${formatPaisa(totalExcl)}</b></td>
            <td></td>
            <td><b>${formatWhole(totalTax)}</b></td>
            <td><b>${formatPaisa(totalTax)}</b></td>
            <td><b>${formatWhole(totalIncl)}</b></td>
            <td><b>${formatPaisa(totalIncl)}</b></td>
          </tr>
        </tbody>
      </table>
      <div class="dashboard-invoice-fbr-line">FBR Invoice #: </div>
      <div class="dashboard-invoice-verification">
        <img class="dashboard-invoice-logo" src="${fbrLogo}" alt="Logo" />
        <div class="dashboard-invoice-qr-placeholder"></div>
        <div></div>
        <div class="dashboard-invoice-summary">
          <div><b>Sales Tax:</b></div>
          <div>${formatWhole(totalTax)}.${formatPaisa(totalTax)}</div>
          <div><b>Net Tax Inc.Value:</b></div>
          <div>${formatWhole(totalIncl)}.${formatPaisa(totalIncl)}</div>
        </div>
      </div>
      <div class="dashboard-invoice-signature">
        <div>Signature: <span>&nbsp;</span></div>
        <div>Name &amp; Designation: <span>&nbsp;</span></div>
      </div>
    </div>
  `;
};

const buildModernDashboardInvoicePrintHtml = async (invoice, design = {}, globalData = {}) => {
  const items = Array.isArray(invoice?.items) ? invoice.items : [];
  const totalExcl = items.reduce((sum, item) => sum + toNumber(item.valueSalesExcludingST), 0);
  const totalTax = items.reduce((sum, item) => sum + toNumber(item.salesTaxApplicable), 0);
  const furtherTax = items.reduce((sum, item) => sum + toNumber(item.furtherTax), 0);
  const extraTax = items.reduce((sum, item) => sum + toNumber(item.extraTax), 0);
  const fedPayable = items.reduce((sum, item) => sum + toNumber(item.fedPayable), 0);
  const salesTaxWithheldAtSource = items.reduce((sum, item) => sum + toNumber(item.salesTaxWithheldAtSource), 0);
  const discount = items.reduce((sum, item) => sum + toNumber(item.discount), 0);
  const getFirstInvoiceValue = (...keys) => {
    for (const key of keys) {
      if (invoice?.[key] !== undefined && invoice?.[key] !== null && invoice?.[key] !== "") return invoice[key];
    }
    return "";
  };
  const sumItemValues = (...keys) => items.reduce((sum, item) => {
    for (const key of keys) {
      if (item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== "") return sum + toNumber(item[key]);
    }
    return sum;
  }, 0);
  const incomeTax236G236H = toNumber(getFirstInvoiceValue(
    "incomeTax236G236H",
    "income_tax_236g_236h",
    "incomeTax236G",
    "income_tax_236g",
    "incomeTax236H",
    "income_tax_236h",
    "tax236G236H",
    "tax_236g_236h"
  )) || sumItemValues("incomeTax236G236H", "income_tax_236g_236h", "incomeTax236G", "income_tax_236g", "incomeTax236H", "income_tax_236h", "tax236G236H", "tax_236g_236h");
  const incomeTaxAdd = toNumber(getFirstInvoiceValue(
    "incomeTaxAdv",
    "income_tax_adv",
    "incomeTaxAdvance",
    "income_tax_advance",
    "advanceIncomeTax",
    "advance_income_tax",
    "incomeTaxAdd",
    "income_tax_add"
  )) || sumItemValues("incomeTaxAdv", "income_tax_adv", "incomeTaxAdvance", "income_tax_advance", "advanceIncomeTax", "advance_income_tax", "incomeTaxAdd", "income_tax_add");
  const salesBeforeDiscount = items.reduce((sum, item) => {
    const discountValue = toNumber(item.discount);
    return sum + toNumber(item.valueSalesExcludingST) + discountValue;
  }, 0);
  const totalIncl = totalExcl + totalTax + furtherTax - discount;
  const buyerSalesTaxNo = invoice?.buyerSalesTaxNo || invoice?.buyer_sales_tax_no || invoice?.buyerSalesTaxNumber || "";
  const companyLogo = design?.logo || "";
  const sellerBusinessName = invoice?.sellerBusinessName || invoice?.seller_business_name || globalData?.company_name || globalData?.full_name || "";
  const sellerNTNCNIC = invoice?.sellerNTNCNIC || invoice?.seller_ntn_cnic || globalData?.ntn_cnic || "";
  const sellerAddress = invoice?.sellerAddress || invoice?.seller_address || globalData?.business_address || globalData?.city || "";
  const sellerProvince = invoice?.sellerProvince || invoice?.seller_province || globalData?.province || "";
  const buyerBusinessName = invoice?.buyerBusinessName || invoice?.buyer_business_name || "";
  const buyerNTNCNIC = invoice?.buyerNTNCNIC || invoice?.buyer_ntn_cnic || buyerSalesTaxNo || "";
  const buyerAddress = invoice?.buyerAddress || invoice?.buyer_address || "";
  const buyerProvince = invoice?.buyerProvince || invoice?.buyer_province || "";
  const isDesignBlockVisible = (blockId) => getDesignBlock(design, blockId)?.visible !== false;
  const showCompanyLogo = isDesignBlockVisible("company_logo");
  const showSellerContact = isDesignBlockVisible("seller_contact");
  const showInvoiceNote = isDesignBlockVisible("invoice_note");
  const showAdditionalInfo = isDesignBlockVisible("additional_info");
  const logoBlock = getDesignBlock(design, "company_logo") || {};
  const logoWidth = Number(logoBlock.w) || 132;
  const logoHeight = Number(logoBlock.h) || 98;
  const logoCenterX = (Number(logoBlock.x) || 0) + (logoWidth / 2);
  const logoTopOffset = Math.round((Number(logoBlock.y ?? 34) - 34) * 1.3);
  const logoSlotStyle = `top:${logoTopOffset}px;left:${((logoCenterX * 100) / DESIGN_CANVAS_WIDTH).toFixed(2)}%;width:${((logoWidth * 100) / DESIGN_CANVAS_WIDTH).toFixed(2)}%;height:${Math.round(logoHeight * 1.06)}px;`;
  const fbrInvoiceNo = invoice?.fbrinvoicenumber || invoice?.fbr_invoice_number || "";
  const sellerEmail = design?.sellerContactEmail || invoice?.sellerEmail || invoice?.seller_email || invoice?.email || globalData?.email || "";
  const sellerPhone = design?.sellerContactPhone || invoice?.sellerPhone || invoice?.seller_phone || invoice?.phone || invoice?.contact || globalData?.phone || globalData?.mobile || "";
  const qrValue = fbrInvoiceNo;
  const qrDataUrl = qrValue
    ? await QRCode.toDataURL(String(qrValue), { width: 114, margin: 0, errorCorrectionLevel: "H" }).catch(() => "")
    : "";
  const formatMoney = (value) => toNumber(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatInvoiceDate = (value) => {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD-MM-YYYY") : value || "";
  };
  const formatTaxPeriod = (value, fallbackDate) => {
    const raw = String(value || "").trim();
    const yearMonth = raw.match(/^(\d{4})[-/](\d{1,2})$/);
    if (yearMonth) return `${yearMonth[2].padStart(2, "0")}/${yearMonth[1]}`;
    const monthYear = raw.match(/^(\d{1,2})[-/](\d{4})$/);
    if (monthYear) return `${monthYear[1].padStart(2, "0")}/${monthYear[2]}`;
    if (raw) return raw;
    const parsed = dayjs(fallbackDate);
    return parsed.isValid() ? parsed.format("MM/YYYY") : "";
  };
  const getItemColumnLabel = (key) => INVOICE_ITEM_FIELD_OPTIONS.find((field) => field.key === key)?.label || key;
  const getItemColumnValue = (item, key, index) => {
    const quantity = toNumber(item.quantity);
    const valueExcl = toNumber(item.valueSalesExcludingST);
    const tax = toNumber(item.salesTaxApplicable);
    const itemFurtherTax = toNumber(item.furtherTax);
    const itemDiscount = toNumber(item.discount);
    const moneyFields = new Set([
      "unitPrice",
      "lineTotal",
      "totalValues",
      "valueSalesExcludingST",
      "fixedNotifiedValueOrRetailPrice",
      "salesTaxApplicable",
      "salesTaxWithheldAtSource",
      "extraTax",
      "furtherTax",
      "fedPayable",
      "discount",
      "totalValueIncludingSalesTax",
    ]);

    if (key === "sr") return index + 1;
    if (key === "unitPrice") return formatMoney(quantity ? valueExcl / quantity : toNumber(item.fixedNotifiedValueOrRetailPrice || item.totalValues));
    if (key === "lineTotal") return formatMoney(valueExcl + itemDiscount);
    if (key === "totalValueIncludingSalesTax") return formatMoney(valueExcl + tax + itemFurtherTax - itemDiscount);
    if (key === "quantity") return quantity ? quantity.toLocaleString(undefined, { maximumFractionDigits: 4 }) : item.quantity;
    if (moneyFields.has(key)) return formatMoney(item[key]);
    return item[key] ?? "";
  };
  const configuredColumns = Array.isArray(design?.itemColumns) && design.itemColumns.length
    ? design.itemColumns
    : DEFAULT_ITEM_COLUMNS.map((key) => ({ key, visible: true }));
  const visibleItemColumns = [
    ...configuredColumns.filter((column) => column.visible !== false),
    ...((Array.isArray(design?.customItemColumns) ? design.customItemColumns : []).filter((column) => column.visible !== false)),
  ];
  const breakdownValues = {
    salesBeforeDiscount,
    totalExcl,
    discount,
    totalTax,
    furtherTax,
    extraTax,
    fedPayable,
    salesTaxWithheldAtSource,
    incomeTax236G236H,
    incomeTaxAdd,
    grossInvoiceValue: totalIncl,
    netPayableAmount: totalIncl,
  };
  const configuredBreakdownRows = Array.isArray(design?.breakdownRows) && design.breakdownRows.length ? design.breakdownRows : DEFAULT_BREAKDOWN_ROWS;
  const visibleBreakdownRows = configuredBreakdownRows.filter((row) => row.visible !== false && row.valueType !== "words");
  const showTotalBreakdown = visibleBreakdownRows.length > 0;
  const showAmountWords = configuredBreakdownRows.find((row) => row.id === "amountWords")?.visible !== false;
  const additionalInfoRows = [
    { label: "Terms of Sale:", value: invoice?.termsOfSale || invoice?.terms_of_sale || "" },
    { label: "Invoice Ref No.:", value: invoice?.invoiceRefNo || invoice?.invoice_ref_no || "" },
    { label: "Buyer Registration Type:", value: invoice?.buyerRegistrationType || invoice?.buyer_registration_type || "" },
    { label: "Scenario ID:", value: invoice?.scenarioId || invoice?.scenario_id || "" },
  ];

  const headerCells = visibleItemColumns.map((column) => `<th>${escapeHtml(column.label || getItemColumnLabel(column.key))}</th>`).join("");
  const rows = items.map((item, index) => `
    <tr>
      ${visibleItemColumns.map((column) => `<td>${escapeHtml(column.id ? column.value : getItemColumnValue(item, column.key, index))}</td>`).join("")}
    </tr>
  `).join("");
  const breakdownRowsHtml = visibleBreakdownRows.map((row) => {
    const value = row.valueType === "custom" ? row.value : formatMoney(breakdownValues[row.id]);
    return `<tr class="${row.isNet ? "modern-net" : ""}"><td>${escapeHtml(row.label)}</td><td>${escapeHtml(value || "-")}</td></tr>`;
  }).join("");

  return `
    <style>
      html,
      body {
        margin: 0;
        padding: 0;
      }
      .dashboard-invoice-modern {
        width: 794px;
        min-height: 1123px;
        padding: 48px 34px 40px;
        background: #fff;
        color: #111;
        font-family: Arial, Helvetica, sans-serif;
        font-size: 14px;
        box-sizing: border-box;
      }
      .modern-header {
        position: relative;
        display: grid;
        grid-template-columns: 1fr 170px 1fr;
        align-items: start;
        min-height: 130px;
      }
      .modern-di {
        display: flex;
        align-items: center;
        gap: 5px;
        color: #7c8795;
        font-size: 20px;
        font-weight: 700;
        line-height: 1;
        letter-spacing: -0.2px;
      }
      .modern-di-mark {
        width: 33px;
        height: 33px;
        border-left: 7px solid #5bb970;
        border-bottom: 7px solid #5bb970;
        border-top: 7px solid #3266ad;
        border-right: 7px solid #3266ad;
        border-radius: 0 17px 17px 0;
        box-sizing: border-box;
      }
      .modern-seller-brand {
        margin-top: 14px;
        line-height: 1.27;
      }
      .modern-seller-brand b {
        display: block;
        font-size: 16px;
        margin-bottom: 7px;
      }
      .modern-company-logo {
        width: 100%;
        height: 100%;
        object-fit: contain;
        margin: 0 auto;
        display: block;
        transform: scale(1.14);
      }
      .modern-logo-slot {
        position: absolute;
        top: 0;
        left: 50%;
        width: 190px;
        height: 104px;
        transform: translateX(-50%);
        text-align: center;
        overflow: hidden;
      }
      .modern-fbr {
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
        gap: 24px;
      }
      .modern-fbr-stack {
        text-align: center;
        padding-top: 8px;
      }
      .modern-fbr-stack img {
        width: 86px;
        height: 70px;
        object-fit: contain;
      }
      .modern-qr {
        width: 82px;
        height: 82px;
        object-fit: contain;
        image-rendering: pixelated;
      }
      .modern-qr-fallback {
        width: 82px;
        height: 82px;
        background:
          repeating-linear-gradient(90deg, #000 0 5px, #fff 5px 10px),
          repeating-linear-gradient(0deg, transparent 0 8px, rgba(255,255,255,0.55) 8px 13px);
        border: 1px solid #111;
        box-sizing: border-box;
      }
      .modern-header-right {
        grid-column: 3;
        justify-self: end;
      }
      .modern-title {
        text-align: center;
        font-size: 20px;
        font-weight: 800;
        margin: 0 0 34px;
      }
      .modern-info {
        display: grid;
        grid-template-columns: 1.05fr 1.05fr 0.95fr;
        gap: 42px;
        margin-bottom: 22px;
      }
      .modern-info h2 {
        font-size: 16px;
        margin: 0 0 18px;
        font-weight: 800;
      }
      .modern-kv {
        display: grid;
        grid-template-columns: 95px 1fr;
        gap: 4px 13px;
        line-height: 1.23;
      }
      .modern-kv b {
        font-weight: 800;
      }
      .modern-section-heading {
        font-size: 16px;
        font-weight: 800;
        margin: 0 0 16px;
      }
      .modern-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
        margin: 0 0 34px;
        page-break-inside: auto;
      }
      .modern-table th,
      .modern-table td {
        border: 1px solid #c9c9c9;
        padding: 10px 8px;
        vertical-align: middle;
      }
      .modern-table th {
        background: #f7f7f7;
        font-size: 8px;
        font-weight: 800;
        text-align: center;
      }
      .modern-table td {
        font-size: 8px;
        text-align: right;
      }
      .modern-table td:nth-child(3) {
        text-align: left;
      }
      .modern-table thead {
        display: table-header-group;
      }
      .modern-table tr {
        page-break-inside: avoid;
        break-inside: avoid;
      }
      .modern-lower {
        display: grid;
        grid-template-columns: 1.23fr 1fr;
        gap: 20px;
        align-items: start;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      .modern-panel {
        border: 1px solid #c9c9c9;
        margin-bottom: 20px;
      }
      .modern-panel-title {
        background: #f7f7f7;
        border-bottom: 1px solid #c9c9c9;
        padding: 10px 9px;
        font-weight: 800;
        font-size: 12px;
      }
      .modern-note {
        min-height: 66px;
        padding: 10px 9px;
        line-height: 1.45;
        font-size: 12px;
      }
      .modern-custom-row {
        display: grid;
        grid-template-columns: 160px 1fr;
        min-height: 24px;
        border-top: 1px solid #d8d8d8;
        align-items: center;
      }
      .modern-custom-row b {
        padding: 5px 9px;
        color: #374151;
        font-size: 12px;
      }
      .modern-custom-row span {
        padding: 5px 9px;
        color: #444;
        font-size: 13px;
      }
      .modern-total-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
      }
      .modern-total-table th,
      .modern-total-table td {
        border: 1px solid #c9c9c9;
        padding: 11px 9px;
      }
      .modern-total-table th {
        background: #f7f7f7;
        text-align: left;
        font-size: 12px;
        font-weight: 800;
      }
      .modern-total-table td {
        text-align: right;
        font-size: 12px;
      }
      .modern-total-table td:first-child {
        text-align: left;
      }
      .modern-net td {
        background: #f7f7f7;
        font-weight: 800;
      }
      .modern-words {
        border: 1px solid #c9c9c9;
        border-top: 0;
        padding: 9px;
        font-size: 11px;
        line-height: 1.1;
      }
      .modern-words-standalone {
        border-top: 1px solid #c9c9c9;
      }
      .modern-words b {
        display: block;
        margin-bottom: 3px;
      }
      .modern-page-footer {
        display: none;
      }
      @media print {
        @page {
          size: A4 portrait;
          margin: 0;
          @bottom-right {
            content: "Page " counter(page) " of " counter(pages);
            font-family: Arial, Helvetica, sans-serif;
            font-size: 9px;
            color: #6b7280;
          }
        }
        .dashboard-invoice-modern {
          width: 210mm;
          min-height: 297mm;
          padding: 13mm 9mm 16mm;
        }
        .modern-lower {
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .modern-page-footer {
          display: block;
          position: fixed;
          right: 9mm;
          bottom: 6mm;
          color: #6b7280;
          font-size: 9px;
          font-family: Arial, Helvetica, sans-serif;
        }
        .modern-page-footer::after {
          content: "Page " counter(page) " of " counter(pages);
        }
      }
    </style>
    <div class="dashboard-invoice-modern">
      <div class="modern-header">
        <div>
          <div class="modern-di"><span class="modern-di-mark"></span><span>DIGITAL<br />INVOICING</span></div>
          <div class="modern-seller-brand">
            <b>${escapeHtml(sellerBusinessName)}</b>
            ${showSellerContact && sellerEmail ? `${escapeHtml(sellerEmail)}<br />` : ""}
            ${showSellerContact && sellerPhone ? `${escapeHtml(sellerPhone)}` : ""}
          </div>
        </div>
        <div class="modern-logo-slot" style="${logoSlotStyle}">
          ${showCompanyLogo && companyLogo
            ? `<img class="modern-company-logo" src="${companyLogo}" alt="Company logo" />`
            : ""}
        </div>
        <div class="modern-header-right">
          <div class="modern-fbr">
            <div class="modern-fbr-stack"><img src="${fbrLogo}" alt="FBR Digital Invoicing" /></div>
            ${qrDataUrl ? `<img class="modern-qr" src="${qrDataUrl}" alt="FBR QR Code" />` : `<div class="modern-qr-fallback"></div>`}
          </div>
        </div>
      </div>

      <h1 class="modern-title">Sales Tax Invoice</h1>

      <div class="modern-info">
        <div>
          <h2>Seller Information</h2>
          <div class="modern-kv">
            <b>Business Name:</b><span>${escapeHtml(sellerBusinessName)}</span>
            <b>Registration No.:</b><span>${escapeHtml(sellerNTNCNIC)}</span>
            <b>STRN No.:</b><span>${escapeHtml(invoice?.sellerSTRN || invoice?.seller_strn || globalData?.strn || "")}</span>
            <b>Address:</b><span>${escapeHtml(sellerAddress)}</span>
            <b>Province:</b><span>${escapeHtml(sellerProvince)}</span>
          </div>
        </div>
        <div>
          <h2>Buyer Information</h2>
          <div class="modern-kv">
            <b>Business Name:</b><span>${escapeHtml(buyerBusinessName)}</span>
            <b>Registration No.:</b><span>${escapeHtml(buyerNTNCNIC)}</span>
            <b>STRN No.:</b><span>${escapeHtml(invoice?.buyerSTRN || invoice?.buyer_strn || invoice?.buyerSalesTaxNo || invoice?.buyer_sales_tax_no || "")}</span>
            <b>Address:</b><span>${escapeHtml(buyerAddress)}</span>
            <b>Province:</b><span>${escapeHtml(buyerProvince)}</span>
          </div>
        </div>
        <div>
          <h2>Invoice Summary</h2>
          <div class="modern-kv">
            <b>FBR Invoice No.:</b><span>${escapeHtml(fbrInvoiceNo || "N/A")}</span>
            <b>Seller Invoice No.:</b><span>${escapeHtml(invoice?.invoiceNumber)}</span>
            <b>Invoice Date:</b><span>${escapeHtml(formatInvoiceDate(invoice?.invoiceDate))}</span>
            <b>Invoice Type:</b><span>${escapeHtml(invoice?.invoiceType || "Sale Invoice")}</span>
            <b>Tax Period:</b><span>${escapeHtml(formatTaxPeriod(invoice?.taxPeriod, invoice?.invoiceDate))}</span>
          </div>
        </div>
      </div>

      <div class="modern-section-heading">Item Details</div>
      <table class="modern-table">
        <colgroup>
          ${visibleItemColumns.map(() => `<col style="width:${(100 / Math.max(visibleItemColumns.length, 1)).toFixed(2)}%;" />`).join("")}
        </colgroup>
        <thead>
          <tr>${headerCells}</tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>

      <div class="modern-lower">
        <div>
          ${showInvoiceNote ? `
            <div class="modern-panel">
              <div class="modern-panel-title">INVOICE NOTE</div>
              <div class="modern-note">${escapeHtml(invoice?.remarks || "")}</div>
            </div>
          ` : ""}
          ${showAdditionalInfo ? `
            <div class="modern-panel">
              <div class="modern-panel-title">ADDITIONAL INFORMATION</div>
              ${additionalInfoRows.map((row) => `
                <div class="modern-custom-row"><b>${escapeHtml(row.label)}</b><span>${escapeHtml(row.value || "-")}</span></div>
              `).join("")}
            </div>
          ` : ""}
        </div>
        <div>
          ${showTotalBreakdown ? `
            <table class="modern-total-table">
              <colgroup>
                <col style="width: 62%;" />
                <col style="width: 38%;" />
              </colgroup>
              <thead>
                <tr><th>Total Breakdown</th><th>Value (PKR)</th></tr>
              </thead>
              <tbody>
                ${breakdownRowsHtml}
              </tbody>
            </table>
          ` : ""}
          ${showAmountWords ? `
            <div class="modern-words ${showTotalBreakdown ? "" : "modern-words-standalone"}">
              <b>Net Payable Amount (In Words):</b>
              ${escapeHtml(amountToWords(totalIncl))}
            </div>
          ` : ""}
        </div>
      </div>
      <div class="modern-page-footer"></div>
    </div>
  `;
};

const dashboardDesignBlockCss = (design, id, selector) => {
  const block = getDesignBlock(design, id);
  if (!block) return "";
  if (block.visible === false) return `#dashboard-printable-content ${selector}{display:none!important;}`;
  const heightRule = id === "items_table"
    ? "height:auto!important;max-height:none!important;"
    : `height:${toPrintMm(block.h, "y")}!important;`;

  return `
    #dashboard-printable-content ${selector}{
      position:absolute!important;
      left:${toPrintMm(block.x, "x")}!important;
      top:${toPrintMm(block.y, "y")}!important;
      width:${toPrintMm(block.w, "x")}!important;
      min-width:0!important;
      ${heightRule}
      overflow:hidden!important;
      margin:0!important;
      box-sizing:border-box!important;
    }
  `;
};

const buildDashboardDesignCss = (design) => {
  if (!Array.isArray(design?.blocks)) return "";

  return `
    <style>
      #dashboard-printable-content .dashboard-invoice-print{
        position:relative!important;
        min-height:277mm!important;
      }
      #dashboard-printable-content .konnect-letterhead-bg{
        position:absolute!important;
        inset:0!important;
        width:100%!important;
        height:100%!important;
        object-fit:cover!important;
        z-index:0!important;
        pointer-events:none!important;
      }
      #dashboard-printable-content .dashboard-invoice-print > :not(.konnect-letterhead-bg){
        position:relative;
        z-index:1;
      }
      #dashboard-printable-content .dashboard-invoice-parties,
      #dashboard-printable-content .dashboard-invoice-verification{
        display:block!important;
        margin:0!important;
      }
      ${dashboardDesignBlockCss(design, "invoice_title", ".dashboard-invoice-title")}
      ${dashboardDesignBlockCss(design, "invoice_meta", ".dashboard-invoice-top")}
      ${dashboardDesignBlockCss(design, "seller_info", ".dashboard-invoice-parties > div:first-child")}
      ${dashboardDesignBlockCss(design, "buyer_info", ".dashboard-invoice-parties > div:nth-child(2)")}
      ${dashboardDesignBlockCss(design, "description_code", ".dashboard-invoice-description-code")}
      ${dashboardDesignBlockCss(design, "items_table", ".dashboard-invoice-print > table")}
      ${dashboardDesignBlockCss(design, "fbr_invoice", ".dashboard-invoice-fbr-line")}
      ${dashboardDesignBlockCss(design, "verification", ".dashboard-invoice-verification > .dashboard-invoice-logo")}
      ${dashboardDesignBlockCss(design, "totals", ".dashboard-invoice-summary")}
      ${dashboardDesignBlockCss(design, "signature", ".dashboard-invoice-signature")}
      #dashboard-printable-content .konnect-custom-logo{
        position:absolute!important;
        z-index:3!important;
        margin:0!important;
        box-sizing:border-box!important;
      }
      #dashboard-printable-content .konnect-custom-logo img{
        width:100%!important;
        height:100%!important;
        object-fit:contain!important;
        display:block!important;
      }
    </style>
  `;
};

const buildDashboardDesignLogoHtml = (design) => {
  const logoBlock = getDesignBlock(design, "company_logo");
  if (!design?.logo || logoBlock?.visible === false) return "";

  return `
    <div
      class="konnect-custom-logo"
      style="left:${toPrintMm(logoBlock?.x ?? 42, "x")};top:${toPrintMm(logoBlock?.y ?? 38, "y")};width:${toPrintMm(logoBlock?.w ?? 74, "x")};height:${toPrintMm(logoBlock?.h ?? 36, "y")};"
    >
      <img src="${design.logo}" alt="Company logo" />
    </div>
  `;
};

const applyDashboardDesignToHtml = (html, design, mode = "normal") => {
  const template = document.createElement("template");
  template.innerHTML = html;
  const page = template.content.querySelector(".dashboard-invoice-print");

  if (page && mode === "preprinted") {
    page.style.paddingTop = `${Number(design?.letterheadTop || 165)}px`;
    page.style.boxSizing = "border-box";
  }

  if (page && design?.letterhead) {
    const background = document.createElement("img");
    background.className = "konnect-letterhead-bg";
    background.alt = "Letterhead";
    background.src = design.letterhead;
    page.prepend(background);
  }

  return `${buildDashboardDesignCss(design)}${buildDashboardDesignLogoHtml(design)}${template.innerHTML}`;
};

const getDashboardDesignForPrintMode = (design, mode) => {
  if (mode === "normal") return {};
  if (mode === "logo") return { ...design, letterhead: "" };
  if (mode === "preprinted") return { ...design, logo: "", letterhead: "" };
  return design;
};

const getDashboardPrintDesign = async (globalData) => {
  try {
    const savedDesign = await getInvoiceDesignApi();
    if (savedDesign?.blocks) return saveLocalInvoiceDesign(savedDesign, globalData);
  } catch {
    // Use cached local design until the backend endpoint is available.
  }

  return loadLocalInvoiceDesign(globalData);
};

/** Map the file's headers to our expected keys (case/space/underscore tolerant) */
const buildHeaderMapper = (fileHeaders, targets) => {
  const fileCanonToActual = {};
  fileHeaders.forEach((h) => (fileCanonToActual[canon(h)] = h));
  const mapper = {};
  targets.forEach((expectedKey) => {
    const possibleHeaders = [
      expectedKey,
      FIELD_LABELS[expectedKey],
      ...(FIELD_ALIASES[expectedKey] || []),
    ].filter(Boolean);
    const matchedHeader = possibleHeaders
      .map((header) => fileCanonToActual[canon(header)])
      .find(Boolean);
    if (matchedHeader) mapper[expectedKey] = matchedHeader; // expected -> actual in file
  });
  return mapper;
};

/** Apply the mapper to one row (so our keys always exist) */
const remapRow = (row, mapper) => {
  const out = { ...row };
  Object.keys(mapper).forEach((expected) => {
    const actual = mapper[expected];
    if (actual in row) out[expected] = normalizeExcelValue(expected, row[actual]);
  });
  return out;
};

/** Forward-fill invoiceNumber and summary fields for rows where they are blank. */
function forwardFillRows(rows, summaryKeys, groupBy = "invoiceNumber") {
  let lastGroup = null;
  const lastSummary = {};
  return rows
    .map((r0) => {
      const r = { ...r0 };

      // carry down invoiceNumber / group key
      if (r[groupBy] == null || r[groupBy] === "") {
        r[groupBy] = lastGroup;
      } else {
        lastGroup = r[groupBy];
      }

      // carry down summary fields
      summaryKeys.forEach((f) => {
        if (r[f] == null || r[f] === "") {
          if (lastSummary[f] != null) r[f] = lastSummary[f];
        } else {
          lastSummary[f] = r[f];
        }
      });

      return r;
    })
    .filter((r) => r[groupBy] != null && r[groupBy] !== "");
}

function parseGroupedInvoices(rows, summaryFields, itemFields, groupByField) {
  const grouped = {};
  rows.forEach(row => {
    const key = row[groupByField];
    if (!key) return;
    if (!grouped[key]) {
      grouped[key] = {
        ...Object.fromEntries(summaryFields.map(f => [f, row[f] ?? ""])),
        items: []
      };
    }
    grouped[key].items.push(
      Object.fromEntries(itemFields.map(f => [f, row[f] ?? ""]))
    );
  });
  return Object.values(grouped).map(inv => ({
    ...inv,
    fbrinvoicenumber: "",
    FBRqrcode: "",
    validation:"",
    alreadySubmitted: false,
    disabledReason: "",
    remarks:"",
    expanded: false,
    invoiceNumber: inv.invoiceNumber || "",
  }));
}

const Dashboard = () => {
  const fileInput = useRef();
  const dashboardPrintRef = useRef();
  const { setLoader,setGlobalData,globalData ,setInvoices,invoices} = useData(); 
  const scenarioOptions = Array.isArray(globalData?.business_scenarios)
    ? globalData.business_scenarios.map((row) => row?.scenario_id || row?.scenarioId).filter(Boolean)
    : [];
  const isNonAdminUser = String(globalData?.client_role || "admin") === "non_admin";
  const isAdminUser = !isNonAdminUser;
  const registrationTypes = ["Registered", "Unregistered"];
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState("");
  const [dashboardPrintHtml, setDashboardPrintHtml] = useState("");
  const [dashboardPrintChoiceInvoice, setDashboardPrintChoiceInvoice] = useState(null);
  const [selected, setSelected] = useState([]);
  const [progress, setProgress] = useState({ sent: 0, total: 0 });
  const [editIdx, setEditIdx] = useState(null);
  const [editInvoice, setEditInvoice] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [submittedUploadSummary, setSubmittedUploadSummary] = useState("");
  const [scenarioCheckRows, setScenarioCheckRows] = useState([]);
  const [scenarioCheckSummary, setScenarioCheckSummary] = useState("");
  const [invoiceTypeOptions, setInvoiceTypeOptions] = useState([]);
  const [approvalRows, setApprovalRows] = useState([]);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [cancelApprovalRow, setCancelApprovalRow] = useState(null);
  const [cancelRemarks, setCancelRemarks] = useState("");
  const editInvoiceTypeOptions = Array.from(new Set([
    ...invoiceTypeOptions.map((option) => option.docDescription).filter(Boolean),
    editInvoice?.invoiceType,
    "Sale Invoice",
  ].filter(Boolean)));
  const baseProvinceOptions = [
    "PUNJAB",
    "SINDH",
    "KHYBER PAKHTUNKHWA",
    "BALOCHISTAN",
    "ISLAMABAD CAPITAL TERRITORY",
    "AZAD JAMMU AND KASHMIR",
    "GILGIT BALTISTAN",
  ];
  const provinceOptions = Array.from(new Set([
    editInvoice?.sellerProvince,
    editInvoice?.buyerProvince,
    globalData?.province,
    ...baseProvinceOptions,
  ].filter(Boolean)));

  // Item editing
  const [editItemDialogOpen, setEditItemDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState({});
  const [editItemIdx, setEditItemIdx] = useState(null);
  const [editInvoiceIdx, setEditInvoiceIdx] = useState(null);
  const configuredHsCodeOptions = Array.isArray(globalData?.hs_codes) ? globalData.hs_codes : [];
  const editHsCodeOptions = Array.from(new Set([
    ...configuredHsCodeOptions.map(getHsCode).filter(Boolean),
    editItem?.hsCode,
  ].filter(Boolean)));
  const editUomOptions = Array.from(new Set([
    ...configuredHsCodeOptions.map(getHsUom).filter(Boolean),
    editItem?.uoM,
    "Number, Pieces, units",
  ].filter(Boolean)));
  const editSaleTypeOptions = Array.from(new Set([
    ...configuredHsCodeOptions.map(getHsSaleType).filter(Boolean),
    editItem?.saleType,
    "Goods at standard rate (default)",
  ].filter(Boolean)));
  const editRateOptions = Array.from(new Set([
    ...configuredHsCodeOptions.map(getHsRate).filter(Boolean),
    editItem?.rate,
  ].filter(Boolean)));
  const editSroScheduleOptions = Array.from(new Set([
    ...configuredHsCodeOptions.map(getHsSroSchedule).filter(Boolean),
    editItem?.sroScheduleNo,
  ].filter(Boolean)));
  const editSroItemOptions = Array.from(new Set([
    ...configuredHsCodeOptions.map(getHsSroItem).filter(Boolean),
    editItem?.sroItemSerialNo,
  ].filter(Boolean)));
// Add these states to your component
// --- States for Admin Approval ---
const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
const [approvalCode, setApprovalCode] = useState("");
const [approvalError, setApprovalError] = useState("");
const [approvalResolver, setApprovalResolver] = useState(null);
const [isVerifying, setIsVerifying] = useState(false);
const approvalInputRefs = useRef([]);
const handleDashboardPrint = useReactToPrint({
  content: () => dashboardPrintRef.current,
  documentTitle: "Sales-Tax-Invoice",
  pageStyle: `
    @page {
      size: A4 portrait;
      margin: 12mm;
    }
    @media print {
      body {
        margin: 0;
      }
      body * {
        visibility: hidden;
      }
      #dashboard-printable-content, #dashboard-printable-content * {
        visibility: visible;
      }
      #dashboard-printable-content {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
      }
    }
  `,
});

const handlePrintValidatedInvoice = async (invoice, mode = "normal") => {
  const savedDesign = await getDashboardPrintDesign(globalData);
  const design = getDashboardDesignForPrintMode(savedDesign, mode);
  const html = mode === "modern"
    ? await buildModernDashboardInvoicePrintHtml(invoice, savedDesign, globalData)
    : applyDashboardDesignToHtml(buildDashboardInvoicePrintHtml(invoice), design, mode);
  setDashboardPrintHtml(html);
  setDashboardPrintChoiceInvoice(null);
  setTimeout(() => {
    handleDashboardPrint();
  }, 150);
};

useEffect(() => {
  if (!globalData) return;

  getFbrInvoiceTypes(globalData)
    .then((result) => setInvoiceTypeOptions(result || []))
    .catch((error) => {
      console.error("Failed to fetch invoice type options:", error);
      setInvoiceTypeOptions([]);
    });
}, [globalData]);

const loadInvoiceApprovals = async () => {
  if (!globalData?.user_id) return;
  try {
    setApprovalLoading(true);
    const rows = await getInvoiceApprovalsApi();
    setApprovalRows(rows || []);
  } catch (error) {
    console.error("Failed to fetch invoice approvals:", error);
    setApprovalRows([]);
  } finally {
    setApprovalLoading(false);
  }
};

useEffect(() => {
  loadInvoiceApprovals();
}, [globalData?.user_id, globalData?.client_role]);

// Function to trigger the dialog and wait for a result
const requestAdminApproval = () => {
  return new Promise((resolve) => {
    setApprovalCode("");
    setApprovalError("");
    setApprovalDialogOpen(true);
    setApprovalResolver({ resolve }); // Store the promise resolver
    setTimeout(() => approvalInputRefs.current[0]?.focus(), 0);
  });
};

const handleApprovalDigitChange = (index, value) => {
  const digit = value.replace(/\D/g, "").slice(-1);
  const codeChars = approvalCode.padEnd(6, " ").slice(0, 6).split("");
  codeChars[index] = digit || "";
  const nextCode = codeChars.join("");
  setApprovalCode(nextCode);
  setApprovalError("");

  if (digit && index < 5) {
    approvalInputRefs.current[index + 1]?.focus();
  }
};

const handleApprovalKeyDown = (index, event) => {
  const currentValue = approvalCode[index] && approvalCode[index] !== " " ? approvalCode[index] : "";

  if (event.key === "Backspace" && !currentValue && index > 0) {
    approvalInputRefs.current[index - 1]?.focus();
  }

  if (event.key === "ArrowLeft" && index > 0) {
    approvalInputRefs.current[index - 1]?.focus();
  }

  if (event.key === "ArrowRight" && index < 5) {
    approvalInputRefs.current[index + 1]?.focus();
  }

  if (event.key === "Enter" && approvalCode.replace(/\s/g, "").length === 6) {
    handleVerifyApprovalCode();
  }
};

const handleApprovalPaste = (event) => {
  event.preventDefault();
  const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
  if (!pasted) return;

  setApprovalCode(pasted.padEnd(6, " "));
  setApprovalError("");

  const targetIndex = Math.min(pasted.length, 5);
  setTimeout(() => approvalInputRefs.current[targetIndex]?.focus(), 0);
};


const handleVerifyApprovalCode = async () => {
  setIsVerifying(true);
  try {
    const normalizedApprovalCode = approvalCode.replace(/\s/g, "");
    const res = await verifyAdminApproval(normalizedApprovalCode);
    console.log("verifyAdminApproval",res.response)
    // Check for success based on your API response structure
    if (res && (res.response.approved)) {
      setApprovalError("");
      setApprovalDialogOpen(false);
      setIsVerifying(false);
      
      // Resolve with true so the loop proceeds with processInvoiceApi
      approvalResolver.resolve(true); 
    }
    else
    {
          setIsVerifying(false);

            setApprovalError("Invalid approval code. Please try again.");

    }
  } catch (error) {
    setIsVerifying(false);
    setApprovalError("Invalid approval code. Please try again.");
    // We do NOT resolve here; we let the user try again or hit Cancel
  }
};

const handleCancelApproval = () => {
  setApprovalDialogOpen(false);
  setApprovalError("");
  setIsVerifying(false);
  // Resolve with false so the loop skips this invoice
  approvalResolver.resolve(false); 
};

  const pendingFbrSubmitCount = useMemo(
    () => invoices.filter((inv) => inv.validation === "Valid" && !inv.FBRqrcode && !inv.alreadySubmitted).length,
    [invoices]
  );

  const pendingValidationCount = useMemo(
    () => invoices.filter((inv) => inv.validation !== "Valid" && !inv.alreadySubmitted).length,
    [invoices]
  );

  const selectableInvoiceIndexes = useMemo(
    () =>
      invoices
        .map((inv, idx) => (inv.alreadySubmitted ? null : idx))
        .filter((idx) => idx !== null),
    [invoices]
  );

  const selectedPendingFbrSubmitCount = useMemo(
    () => selected.filter((idx) => {
      const inv = invoices[idx];
      return inv && inv.validation === "Valid" && !inv.FBRqrcode && !inv.alreadySubmitted;
    }).length,
    [invoices, selected]
  );

  const selectedPendingValidationCount = useMemo(
    () => selected.filter((idx) => {
      const inv = invoices[idx];
      return inv && inv.validation !== "Valid" && !inv.alreadySubmitted;
    }).length,
    [invoices, selected]
  );

	  const activeFbrSubmitCount = selected.length ? selectedPendingFbrSubmitCount : pendingFbrSubmitCount;
	  const activeValidationCount = selected.length ? selectedPendingValidationCount : pendingValidationCount;

	  const renderInvoiceQueueActions = () => (
	    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
	      {isAdminUser && (
	        <Button
	          variant="contained"
	          color="success"
	          disabled={activeFbrSubmitCount === 0 || progress.total > 0}
	          onClick={() => handleBulkSubmit(null, true)}
	          startIcon={<AddIcon />}
	          sx={{
	            bgcolor: "#16a34a",
	            fontWeight: 900,
	            borderRadius: 1.5,
	            px: 2,
	            minHeight: 40,
	            boxShadow: "0 10px 22px rgba(22, 163, 74, 0.18)",
	            textTransform: "none",
	            "&:hover": { bgcolor: "#15803d", boxShadow: "0 10px 22px rgba(22, 163, 74, 0.18)" },
	          }}
	        >
	          Submit to FBR ({activeFbrSubmitCount})
	        </Button>
	      )}
      <Button
        variant="contained"
        color="primary"
        disabled={activeValidationCount === 0 || progress.total > 0}
        onClick={() => handleBulkSubmit(null, false)}
        startIcon={<CheckCircleIcon />}
        sx={{
          bgcolor: "#2563eb",
          fontWeight: 900,
          borderRadius: 1.5,
          px: 2,
          minHeight: 40,
          boxShadow: "0 10px 22px rgba(37, 99, 235, 0.18)",
          textTransform: "none",
          "&:hover": { bgcolor: "#1d4ed8", boxShadow: "0 10px 22px rgba(37, 99, 235, 0.18)" },
        }}
      >
        Validate ({activeValidationCount})
      </Button>
    </Box>
  );

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await dashboard(setLoader);
        if (res.message === "Success") {
          setGlobalData(res?.response?.detail);
        }
      } catch (error) {}
    };
    fetchDashboard();
  }, []);

  /* -------- Error dialog helper -------- */
  const reportUploadError = (title, bullets = []) => {
    const body = [title, ""].concat(bullets.map((b) => `• ${b}`)).join("\n");
    setDialogContent(body);
    setDialogOpen(true);
  };

  const markAlreadySubmittedInvoices = async (sourceInvoices, isProd) => {
    const submittedInvoiceNumbers = await checkSubmittedInvoicesApi(
      sourceInvoices.map((inv) => inv.invoiceNumber),
      isProd
    );
    const submittedSet = new Set(
      submittedInvoiceNumbers.map((invoiceNumber) => String(invoiceNumber || "").trim())
    );

    return sourceInvoices.map((inv) => {
      const alreadySubmitted = submittedSet.has(String(inv.invoiceNumber || "").trim());
      return {
        ...inv,
        alreadySubmitted,
        disabledReason: alreadySubmitted ? "This invoice is already submitted." : inv.disabledReason || "",
        remarks: alreadySubmitted ? "Already submitted" : inv.remarks || "",
      };
    });
  };

  /* Save invoice (manual) */
  const handleSaveInvoice = async (data) => {
    const invoiceNumber = data.invoiceNumber || `Manual${invoices.length + 1}`;
    let submittedInvoiceNumbers = [];

    try {
      setLoader(true);
      submittedInvoiceNumbers = await checkSubmittedInvoicesApi(
        [invoiceNumber],
        globalData?.is_prod_token
      );
    } catch (err) {
      reportUploadError("Could not verify submitted invoice.", [
        String(err?.message || err) || "Unknown verification error",
      ]);
      return;
    } finally {
      setLoader(false);
    }

    const alreadySubmitted = submittedInvoiceNumbers
      .map((submittedInvoiceNumber) => String(submittedInvoiceNumber || "").trim())
      .includes(String(invoiceNumber || "").trim());

    const mappedItems = (data.items || []).map(item =>
      itemFields.reduce((obj, field) => {
        obj[field] = item[field] ?? "";
        return obj;
      }, {})
    );
    setInvoices((prev) => [
      ...prev,
      {
        ...data,
        items: mappedItems,
        fbrinvoicenumber: "",
        FBRqrcode: "",
        validation:"",
        alreadySubmitted,
        disabledReason: alreadySubmitted ? "This invoice is already submitted." : "",
        remarks: alreadySubmitted ? "Already submitted" : "",
        invoiceNumber,
      },
    ]);
    setSubmittedUploadSummary(
      alreadySubmitted ? "1 manually added invoice is already submitted and disabled." : ""
    );
    setDialogContent(
      alreadySubmitted
        ? "Invoice added, but it is already submitted and has been disabled."
        : "Invoice added! (Not sent to FBR yet.)"
    );
    setDialogOpen(true);
  };

  const getFbrRoute = (fbr) => {
    if (fbr) {
      return globalData?.is_prod_token
        ? "https://gw.fbr.gov.pk/di_data/v1/di/postinvoicedata"
        : "https://gw.fbr.gov.pk/di_data/v1/di/postinvoicedata_sb";
    }

    return globalData?.is_prod_token
      ? "https://gw.fbr.gov.pk/di_data/v1/di/validateinvoicedata"
      : "https://gw.fbr.gov.pk/di_data/v1/di/validateinvoicedata_sb";
  };

  const buildFbrInvoicePayload = (sourceInvoice) => {
    const invoice = { ...sourceInvoice };
    const scenarioId = String(invoice.scenarioId || "").trim().toUpperCase();
    const shouldSendEmptyExtraTax = ["SN005", "SN028"].includes(scenarioId);

    invoice.items = invoice.items.map((item) => {
      const parsedExtraTax = parseExcelNumber(item.extraTax);
      const extraTax =
        shouldSendEmptyExtraTax && (!Number.isFinite(parsedExtraTax) || parsedExtraTax === 0)
          ? ""
          : Number.isFinite(parsedExtraTax)
            ? Number(parsedExtraTax.toFixed(2))
            : 0;

      return {
        ...item,
        quantity: Number(parseExcelNumber(item.quantity).toFixed(3)),
        totalValues: Number(parseExcelNumber(item.totalValues).toFixed(2)),
        valueSalesExcludingST: Number(parseExcelNumber(item.valueSalesExcludingST).toFixed(2)),
        fixedNotifiedValueOrRetailPrice: Number(parseExcelNumber(item.fixedNotifiedValueOrRetailPrice).toFixed(2)),
        salesTaxApplicable: Number(parseExcelNumber(item.salesTaxApplicable).toFixed(2)),
        salesTaxWithheldAtSource: Number(parseExcelNumber(item.salesTaxWithheldAtSource).toFixed(2)),
        extraTax,
        furtherTax: Number(parseExcelNumber(item.furtherTax).toFixed(2)),
        fedPayable: Number(parseExcelNumber(item.fedPayable).toFixed(2)),
        discount: Number(parseExcelNumber(item.discount).toFixed(2)),
      };
    });

    delete invoice.fbrinvoicenumber;
    delete invoice.FBRqrcode;
    delete invoice.validation;
    delete invoice.remarks;
    delete invoice.expanded;
    delete invoice.alreadySubmitted;
    delete invoice.disabledReason;

    if (Array.isArray(invoice.items)) {
      invoice.items = invoice.items.map(
        ({ remarks, invoiceNumber, validation, saleTypeId, rateId, sroScheduleId, ...rest }) => rest
      );
    }

    return invoice;
  };

  const applyFbrResponseToInvoice = (newInvoices, idx, response, fbr) => {
    if (response?.error) {
      newInvoices[idx].fbrinvoicenumber = fbr ? "ERROR" : "";
      newInvoices[idx].validation = "Invalid";
      newInvoices[idx].remarks =
        response.error === "FBR API timeout"
          ? "FBR API timeout. FBR did not respond in time."
          : response.error;
      return;
    }

    const validationStatus = response?.validationResponse?.status || "";
    const validationRemarks = getFbrErrorMessage(response?.validationResponse) || "";

    newInvoices[idx].fbrinvoicenumber = response.invoiceNumber ?? "";
    newInvoices[idx].FBRqrcode = response.invoiceNumber ?? "";
    newInvoices[idx].validation = validationStatus || (validationRemarks ? "Invalid" : "");
    newInvoices[idx].remarks = validationRemarks;

    const statuses = response?.validationResponse?.invoiceStatuses || [];

    newInvoices[idx].items = newInvoices[idx].items.map((item, itemIndex) => {
      const match = statuses.find((s) => Number(s.itemSNo) === itemIndex + 1);
      return match
        ? {
            ...item,
            validation: match.status,
            invoiceNumber: match.invoiceNo,
            remarks: getFbrErrorMessage(match),
          }
        : {
            ...item,
            validation: "",
            invoiceNumber: "",
            remarks: "",
          };
    });
  };

  const processInvoiceIndexes = async (baseInvoices, indexes, fbr, progressLabel) => {
    if (!indexes.length) return baseInvoices;

    const route = getFbrRoute(fbr);
    const newInvoices = [...baseInvoices];
    let completed = 0;

    setLoader(true);
    setProgress({ sent: 0, total: indexes.length, label: progressLabel });

    try {
      for (let i = 0; i < indexes.length; i += FBR_BATCH_SIZE) {
        const batch = indexes.slice(i, i + FBR_BATCH_SIZE);

        await Promise.all(
          batch.map(async (idx) => {
            try {
              if (idx == null || !newInvoices[idx] || newInvoices[idx].alreadySubmitted) {
                return;
              }

              const invoice = buildFbrInvoicePayload(newInvoices[idx]);
              const response = await processInvoiceApi(
                () => {},
                invoice,
                route,
                globalData,
                { timeoutMs: FBR_REQUEST_TIMEOUT_MS }
              );

              applyFbrResponseToInvoice(newInvoices, idx, response, fbr);
              if (
                !fbr &&
                isNonAdminUser &&
                newInvoices[idx]?.validation === "Valid" &&
                !newInvoices[idx]?.alreadySubmitted
              ) {
                try {
                  await saveInvoiceApprovalApi({
                    invoice: newInvoices[idx],
                    validationResponse: response,
                    isProd: globalData?.is_prod_token,
                  });
                  newInvoices[idx].approvalStatus = "pending";
                  newInvoices[idx].remarks = newInvoices[idx].remarks || "Sent to admin for submission.";
                } catch (approvalError) {
                  newInvoices[idx].validation = "Invalid";
                  newInvoices[idx].remarks = approvalError?.message || "Validated, but failed to save for admin approval.";
                }
              }
              setInvoices([...newInvoices]);
            } catch (err) {
              if (idx != null && newInvoices[idx]) {
                newInvoices[idx].fbrinvoicenumber = fbr ? "ERROR" : "";
                newInvoices[idx].validation = "Invalid";
                newInvoices[idx].remarks = String(err?.message || err) || "Failed to process invoice.";
                setInvoices([...newInvoices]);
              }
            } finally {
              completed += 1;
              setProgress({ sent: completed, total: indexes.length, label: progressLabel });
            }
          })
        );
      }
    } finally {
      setLoader(false);
      setProgress({ sent: 0, total: 0, label: "" });
    }

    return newInvoices;
  };

  const summarizeValidationResults = (validatedInvoices, totalCount) => {
    const validCount = validatedInvoices.filter((inv) => !inv.alreadySubmitted && inv.validation === "Valid").length;
    const invalidCount = validatedInvoices.filter((inv) => !inv.alreadySubmitted && inv.validation === "Invalid").length;
    const submittedCount = validatedInvoices.filter((inv) => inv.alreadySubmitted).length;
    const pendingCount = Math.max(totalCount - validCount - invalidCount - submittedCount, 0);
    const failedText = pendingCount
      ? `${invalidCount + pendingCount} need review.`
      : `${invalidCount} need review.`;
    const submittedText = submittedCount
      ? ` ${submittedCount} already submitted.`
      : "";

    setSubmittedUploadSummary(
      `${validCount} of ${totalCount} invoices validated successfully.${submittedText} ${failedText}`
    );
  };

  const extractTextFromPdf = async (file) => {
    const pdfjsLib = await import("pdfjs-dist/legacy/build/pdf");
    const pdfjsWorker = await import("pdfjs-dist/build/pdf.worker.entry");
    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker.default || pdfjsWorker;

    const data = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data }).promise;
    const textPages = [];
    const visualTextPages = [];

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      const pageText = content.items.map((item) => item.str || "").join(" ");
      textPages.push(pageText);
      visualTextPages.push(buildVisualPdfText(content.items));
    }

    const text = textPages.join("\n").trim();
    const visualText = visualTextPages.join("\n").trim();
    if (text.replace(/\s/g, "").length > 80) {
      return { text: visualText || text, method: "text" };
    }

    const Tesseract = await import("tesseract.js");
    const ocrPages = [];

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      setSubmittedUploadSummary(`Reading PDF with OCR... page ${pageNumber} of ${pdf.numPages}`);
      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 2 });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;
      const result = await Tesseract.recognize(canvas, "eng");
      ocrPages.push(result?.data?.text || "");
    }

    return { text: ocrPages.join("\n").trim(), method: "ocr" };
  };

  const processPdfUpload = async (file, resetInput) => {
    if (file.size > PDF_UPLOAD_MAX_SIZE) {
      reportUploadError("PDF file too large.", ["Max allowed: 20 MB"]);
      resetInput();
      return;
    }

    try {
      setLoader(true);
      setSubmittedUploadSummary("Reading PDF invoice...");
      const { text, method } = await extractTextFromPdf(file);

      if (!text || text.replace(/\s/g, "").length < 20) {
        reportUploadError("Could not read invoice data from this PDF.", [
          "This PDF may be too blurry or protected.",
          "Please upload a clearer invoice PDF or use Excel upload.",
        ]);
        return;
      }

      const pdfInvoice = buildPdfInvoiceDraft({
        text,
        fileName: file.name,
        globalData,
        invoiceTypeOptions,
        scenarioOptions,
      });

      let submittedInvoiceNumbers = [];
      try {
        submittedInvoiceNumbers = await checkSubmittedInvoicesApi(
          [pdfInvoice.invoiceNumber],
          globalData?.is_prod_token
        );
      } catch (err) {
        reportUploadError("Could not verify submitted PDF invoice.", [
          String(err?.message || err) || "Unknown verification error",
        ]);
        return;
      }

      const isAlreadySubmitted = submittedInvoiceNumbers
        .map((invoiceNumber) => String(invoiceNumber || "").trim())
        .includes(String(pdfInvoice.invoiceNumber || "").trim());

      const invoicesWithSubmissionStatus = [{
        ...pdfInvoice,
        alreadySubmitted: isAlreadySubmitted,
        disabledReason: isAlreadySubmitted ? "This invoice is already submitted." : "",
        remarks: isAlreadySubmitted ? "Already submitted" : pdfInvoice.remarks,
      }];

      setInvoices(invoicesWithSubmissionStatus);
      setSelected(isAlreadySubmitted ? [] : [0]);
      setProgress({ sent: 0, total: 0 });
      setSubmittedUploadSummary(
        `PDF invoice extracted using ${method === "ocr" ? "OCR" : "PDF text"}. Validating with FBR...`
      );

      const validatedInvoices = await processInvoiceIndexes(
        invoicesWithSubmissionStatus,
        isAlreadySubmitted ? [] : [0],
        false,
        "Validating PDF invoice with FBR"
      );

      summarizeValidationResults(validatedInvoices, 1);
    } catch (err) {
      reportUploadError("Unexpected error while processing PDF.", [
        String(err?.message || err) || "Unknown error",
      ]);
    } finally {
      setLoader(false);
      resetInput();
    }
  };

  /* -------- Robust Excel upload with validations and clear errors -------- */
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    const resetInput = () => { if (e?.target) e.target.value = ""; };

    try {
      if (!file) {
        reportUploadError("No file selected.");
        return;
      }

      const ext = file.name.toLowerCase().split(".").pop();
      if (ext === "pdf") {
        await processPdfUpload(file, resetInput);
        return;
      }

      if (!["xlsx", "xlsm", "xls"].includes(ext)) {
        reportUploadError("Unsupported file type.", [
          `Got: .${ext}`,
          "Please upload an Excel file (.xlsx/.xlsm/.xls) or PDF invoice (.pdf).",
        ]);
        resetInput();
        return;
      }

      if (file.size > 15 * 1024 * 1024) {
        reportUploadError("File too large.", ["Max allowed: 15 MB"]);
        resetInput();
        return;
      }

      // Read workbook
      let wb;
      try {
        const data = await file.arrayBuffer();
        wb = XLSX.read(data, { type: "array", cellText: true, cellDates: true, raw: false });
      } catch (err) {
        reportUploadError("Could not read this Excel file.", [
          String(err?.message || err) || "Unknown read error",
        ]);
        resetInput();
        return;
      }

      // First sheet
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws) {
        reportUploadError("No visible sheets in this workbook.");
        resetInput();
        return;
      }

      // Parse rows (keep nulls for forward-fill)
      const rowsArr = XLSX.utils.sheet_to_json(ws, {
        defval: null,
        raw: false,
      });
      if (!rowsArr.length) {
        reportUploadError("The first sheet is empty.");
        resetInput();
        return;
      }

      // Header mapper (tolerant)
      const allHeaders = Array.from(new Set(rowsArr.flatMap((r) => Object.keys(r))));
      const targets = [...summaryFields, ...itemFields];
      const mapper = buildHeaderMapper(allHeaders, targets);

      // Validate required columns after mapping
      const missing = [];
      REQUIRED_SUMMARY.forEach((k) => { if (!mapper[k]) missing.push(k); });
      REQUIRED_ITEM.forEach((k) => { if (!mapper[k]) missing.push(k); });

      if (missing.length) {
        const found = allHeaders.length ? [`Found columns: ${allHeaders.join(", ")}`] : [];
        reportUploadError(
          "Required columns not found.",
          missing.map((m) => `"${getFieldLabel(m)}" is missing.`).concat(found)
        );
        resetInput();
        return;
      }

      // Remap each row to expected keys
      const normalizedRows = rowsArr.map((r) => remapRow(r, mapper));

      // Forward-fill then group
      const normalized = forwardFillRows(normalizedRows, summaryFields, "invoiceNumber");

      const hasAnyInvoice = normalized.some((r) => r.invoiceNumber != null && r.invoiceNumber !== "");
      if (!hasAnyInvoice) {
        reportUploadError('No "invoiceNumber" values were found after mapping/forward-fill.', [
          "Make sure the first row of each invoice has an invoice number.",
        ]);
        resetInput();
        return;
      }

      const groupedInvoices = parseGroupedInvoices(
        normalized,
        summaryFields,
        itemFields,
        "invoiceNumber"
      );

      if (!groupedInvoices.length) {
        reportUploadError("No invoices could be built from this sheet.", [
          "Check that invoiceNumber and at least one item column (e.g., productDescription) exist.",
        ]);
        resetInput();
        return;
      }

      let submittedInvoiceNumbers = [];
      try {
        setLoader(true);
        submittedInvoiceNumbers = await checkSubmittedInvoicesApi(
          groupedInvoices.map((inv) => inv.invoiceNumber),
          globalData?.is_prod_token
        );
      } catch (err) {
        reportUploadError("Could not verify submitted invoices.", [
          String(err?.message || err) || "Unknown verification error",
        ]);
        resetInput();
        return;
      } finally {
        setLoader(false);
      }

      const submittedSet = new Set(
        submittedInvoiceNumbers.map((invoiceNumber) => String(invoiceNumber || "").trim())
      );
      const invoicesWithSubmissionStatus = groupedInvoices.map((inv) => {
        const isAlreadySubmitted = submittedSet.has(String(inv.invoiceNumber || "").trim());
        return {
          ...inv,
          alreadySubmitted: isAlreadySubmitted,
          disabledReason: isAlreadySubmitted ? "This invoice is already submitted." : "",
          remarks: isAlreadySubmitted ? "Already submitted" : inv.remarks,
        };
      });

      // Success
      setInvoices(invoicesWithSubmissionStatus);
      setSelected(
        invoicesWithSubmissionStatus
          .map((inv, idx) => (inv.alreadySubmitted ? null : idx))
          .filter((idx) => idx !== null)
      );
      setProgress({ sent: 0, total: 0 });
      setSubmittedUploadSummary("Validating uploaded invoices with FBR...");

      const validationIndexes = invoicesWithSubmissionStatus
        .map((inv, idx) => (inv.alreadySubmitted ? null : idx))
        .filter((idx) => idx !== null);

      const validatedInvoices = await processInvoiceIndexes(
        invoicesWithSubmissionStatus,
        validationIndexes,
        false,
        "Validating uploaded invoices with FBR"
      );

      summarizeValidationResults(validatedInvoices, groupedInvoices.length);
    } catch (err) {
      reportUploadError("Unexpected error while processing file.", [
        String(err?.message || err) || "Unknown error",
      ]);
    } finally {
      resetInput(); // allow re-uploading the same file
    }
  };

  /* -------- Edit invoice (summary) -------- */
  const handleEdit = (idx) => {
    if (invoices[idx]?.alreadySubmitted) return;
    setEditInvoice({ ...invoices[idx] });
    setEditIdx(idx);
    setEditDialogOpen(true);
  };
  const handleEditSave = () => {
    const updated = [...invoices];
    summaryFields.forEach(field => {
      updated[editIdx][field] = editInvoice[field];
    });
    setInvoices(updated);
    setEditDialogOpen(false);
    setEditIdx(null);
  };

  const getSummaryFieldOptions = (field) => {
    if (field === "invoiceType") return editInvoiceTypeOptions;
    if (field === "scenarioId") return Array.from(new Set([editInvoice?.scenarioId, ...scenarioOptions].filter(Boolean)));
    if (field === "buyerRegistrationType") return Array.from(new Set([editInvoice?.buyerRegistrationType, ...registrationTypes].filter(Boolean)));
    if (field === "sellerProvince" || field === "buyerProvince") return provinceOptions;
    return [];
  };

  const getItemFieldOptions = (field) => {
    if (field === "hsCode") return editHsCodeOptions;
    if (field === "uoM") return editUomOptions;
    if (field === "saleType") return editSaleTypeOptions;
    if (field === "rate") return editRateOptions;
    if (field === "sroScheduleNo") return editSroScheduleOptions;
    if (field === "sroItemSerialNo") return editSroItemOptions;
    return [];
  };

  const applyHsCodeToEditItem = (hsCodeValue) => {
    const selectedHsCode = configuredHsCodeOptions.find((option) => getHsCode(option) === hsCodeValue);
    setEditItem((prev) => ({
      ...prev,
      hsCode: hsCodeValue,
      productDescription: getHsDescription(selectedHsCode) || prev.productDescription,
      uoM: getHsUom(selectedHsCode) || prev.uoM,
      saleType: getHsSaleType(selectedHsCode) || prev.saleType,
      rate: getHsRate(selectedHsCode) || prev.rate,
      sroScheduleNo: getHsSroSchedule(selectedHsCode) || prev.sroScheduleNo,
      sroItemSerialNo: getHsSroItem(selectedHsCode) || prev.sroItemSerialNo,
    }));
  };

  /* -------- Delete invoice -------- */
  const handleDelete = (idx) => {
    if (invoices[idx]?.alreadySubmitted) return;
    setInvoices(prev => prev.filter((_, i) => i !== idx));
    setSelected(prev => prev.filter(i => i !== idx).map(i => (i > idx ? i - 1 : i)));
  };

  /* -------- Selection -------- */
  const handleSelect = (idx) => {
    if (invoices[idx]?.alreadySubmitted) return;
    setSelected(selected =>
      selected.includes(idx) ? selected.filter(i => i !== idx) : [...selected, idx]
    );
  };
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelected(selectableInvoiceIndexes);
    else setSelected([]);
  };

  /* -------- Expand/collapse -------- */
  const handleExpand = idx => {
    setInvoices(prev =>
      prev.map((inv, i) =>
        i === idx ? { ...inv, expanded: !inv.expanded } : inv
      )
    );
  };

const handleBulkSubmit = async (val, fbr) => {
  if (fbr && isNonAdminUser) {
    alert("Only company admin can submit invoices to FBR.");
    return;
  }

  const sourceIndexes = selected.length ? selected : invoices.map((_, idx) => idx);
  const baseIndexes = fbr
    ? sourceIndexes
        .map((idx) => {
          const inv = invoices[idx];
          return inv?.validation === "Valid" && !inv?.FBRqrcode && !inv?.alreadySubmitted ? idx : null;
        })
        .filter((idx) => idx !== null)
    : sourceIndexes
        .map((idx) => {
          const inv = invoices[idx];
          return inv?.validation !== "Valid" && !inv?.alreadySubmitted ? idx : null;
        })
        .filter((idx) => idx !== null);

  if (baseIndexes.length === 0 && selected.length) {
    alert(fbr ? "No selected validated invoices are available for FBR submission." : "No selected invoices need validation.");
    return;
  }

  if (baseIndexes.length === 0) {
    alert(fbr ? "No validated invoices available for FBR submission." : "No invoices need validation.");
    return;
  }

  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const approvedIndexes = [];
  for (const idx of baseIndexes) {
    if (!fbr || !globalData?.is_approval) {
      approvedIndexes.push(idx);
      continue;
    }

    const invDate = new Date(invoices[idx].invoiceDate);
    const invDateOnly = new Date(invDate.getFullYear(), invDate.getMonth(), invDate.getDate());
    const isFuture = invDateOnly > todayStart;
    const isCurrentMonth =
      invDate.getMonth() === today.getMonth() &&
      invDate.getFullYear() === today.getFullYear();
    const isPreviousMonth =
      today.getMonth() === 0
        ? invDate.getMonth() === 11 && invDate.getFullYear() === today.getFullYear() - 1
        : invDate.getMonth() === today.getMonth() - 1 && invDate.getFullYear() === today.getFullYear();
    const isWithinGracePeriod = today.getDate() <= 5;
    const isValidDate =
      !isFuture && (isCurrentMonth || (isPreviousMonth && isWithinGracePeriod));

    if (isValidDate || await requestAdminApproval()) {
      approvedIndexes.push(idx);
    }
  }

  const processedInvoices = await processInvoiceIndexes(
    invoices,
    approvedIndexes,
    fbr,
    fbr ? "Submitting selected invoices to FBR" : "Validating selected invoices with FBR"
  );

  if (!fbr) {
    summarizeValidationResults(processedInvoices, processedInvoices.length);
    if (isNonAdminUser) {
      await loadInvoiceApprovals();
    }
  } else if (!globalData?.is_prod_token && scenarioCheckRows.length) {
    updateScenarioStatusPanel(processedInvoices);
  }
};

const handleSubmitApproval = async (approvalRow) => {
  const invoiceSource = approvalRow?.invoice_payload;
  if (!invoiceSource) return;

  try {
    setLoader(true);
    const invoice = buildFbrInvoicePayload(invoiceSource);
    const response = await processInvoiceApi(
      () => {},
      invoice,
      getFbrRoute(true),
      globalData,
      { timeoutMs: FBR_REQUEST_TIMEOUT_MS }
    );

    if (response?.error || response?.validationResponse?.status !== "Valid") {
      throw new Error(response?.error || getFbrErrorMessage(response?.validationResponse) || "FBR submission failed.");
    }

    await markInvoiceApprovalSubmittedApi(approvalRow.id);
    await loadInvoiceApprovals();
    setSubmittedUploadSummary(`Invoice ${approvalRow.invoice_number || ""} submitted to FBR.`);
  } catch (error) {
    alert(error?.message || "Failed to submit pending invoice.");
  } finally {
    setLoader(false);
  }
};

const handleCancelApprovalSubmit = async () => {
  if (!cancelApprovalRow || !cancelRemarks.trim()) {
    alert("Cancel remarks are required.");
    return;
  }

  try {
    await cancelInvoiceApprovalApi(cancelApprovalRow.id, cancelRemarks.trim());
    setCancelApprovalRow(null);
    setCancelRemarks("");
    await loadInvoiceApprovals();
  } catch (error) {
    alert(error?.message || "Failed to cancel pending invoice.");
  }
};

const openCancelApprovalDialog = (approvalRow) => {
  setCancelApprovalRow(approvalRow);
  setCancelRemarks("");
};

  

  /* -------- Download Excel with QR images -------- */
  const handleDownloadExcel = async () => {
    const exportData = [];
    for (const inv of invoices) {
      for (const item of inv.items) {
        let qrDataUrl = "";
        if (inv.FBRqrcode) {
          qrDataUrl = await QRCode.toDataURL(inv.FBRqrcode, { width: 100 });
        }
        exportData.push({
          ...summaryFields.reduce((acc, f) => ({ ...acc, [f]: inv[f] }), {}),
          ...EXCEL_EXPORT_ITEM_FIELDS.reduce((acc, f) => ({ ...acc, [f]: item[f] }), {}),
          fbrinvoicenumber: inv.fbrinvoicenumber || "",
          FBRqrcode: qrDataUrl
        });
      }
    }
    const excelHeader = toFriendlyExcelHeaders([...summaryFields, ...EXCEL_EXPORT_ITEM_FIELDS, "fbrinvoicenumber", "FBRqrcode"]);
    const ws = XLSX.utils.json_to_sheet(toFriendlyExcelRows(exportData), { header: excelHeader });
    XLSX.utils.sheet_add_aoa(ws, [excelHeader], { origin: "A1" });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Invoices");
    XLSX.writeFile(wb, "submitted_invoices.xlsx");
  };

  const getConfiguredScenarios = () => {
    const scenarios = Array.isArray(globalData?.business_scenarios) && globalData.business_scenarios.length
      ? globalData.business_scenarios
      : [{ scenario_id: globalData?.scenarioId || "SN001" }];
    return scenarios.map((scenario) => getScenarioId(scenario)).filter(Boolean);
  };

  const isScenarioSubmitted = (invoice) =>
    isPresent(invoice?.FBRqrcode) &&
    invoice.FBRqrcode !== "ERROR" &&
    isPresent(invoice?.fbrinvoicenumber) &&
    invoice.fbrinvoicenumber !== "ERROR";

  const buildScenarioStatusRows = (sourceInvoices = invoices) => {
    const scenarios = getConfiguredScenarios();
    const hsCodes = getConfiguredHsCodes();

    return scenarios.map((scenario, index) => {
      const matchedInvoice = sourceInvoices.find(
        (invoice) => String(invoice?.scenarioId || "").trim().toUpperCase() === String(scenario).trim().toUpperCase()
      );
      const done = Boolean(matchedInvoice && isScenarioSubmitted(matchedInvoice));
      const configuredHsCode = hsCodes[index % Math.max(hsCodes.length, 1)] || {};

      return {
        scenarioId: scenario,
        hsCode: matchedInvoice?.items?.[0]?.hsCode || getHsCode(configuredHsCode) || "",
        status: done ? "Completed" : "Pending",
        remarks: done
          ? matchedInvoice?.fbrinvoicenumber || matchedInvoice?.FBRqrcode || "Submitted to FBR"
          : matchedInvoice?.remarks || matchedInvoice?.items?.[0]?.remarks || "",
      };
    });
  };

  const updateScenarioStatusPanel = (sourceInvoices = invoices) => {
    const rows = buildScenarioStatusRows(sourceInvoices);
    const doneCount = rows.filter((row) => row.status === "Completed").length;
    const pendingCount = Math.max(rows.length - doneCount, 0);
    setScenarioCheckRows(rows);
    setScenarioCheckSummary(`${doneCount} completed, ${pendingCount} pending.`);
  };

  const getConfiguredHsCodes = () => {
    const hsCodes = Array.isArray(globalData?.hs_codes) && globalData.hs_codes.length
      ? globalData.hs_codes
      : [{ hs_code: "0101.2100", uom: "Nos", description: "Sandbox sample item", sale_type: "Goods at standard rate (default)", rate: "18%" }];
    return hsCodes.filter((hsCode) => getHsCode(hsCode) && getHsUom(hsCode));
  };

  const buildSandboxSampleInvoices = () => {
    const scenarios = getConfiguredScenarios();
    const hsCodes = getConfiguredHsCodes();

    const today = dayjs().format("YYYY-MM-DD");
    const invoicesToBuild = [];

    scenarios.forEach((scenario, scenarioIndex) => {
      const hsCode = hsCodes[scenarioIndex % Math.max(hsCodes.length, 1)] || {};
      const rate = getHsRate(hsCode) || "18%";
      const ratePercent = parseRatePercent(rate);
      const valueSalesExcludingST = 1000;
      const salesTaxApplicable = Number(((valueSalesExcludingST * ratePercent) / 100).toFixed(2));
      const invoiceNumber = `SB-${scenario}-${Date.now()}-${scenarioIndex + 1}`;

      invoicesToBuild.push({
        invoiceType: "Sale Invoice",
        invoiceDate: today,
        sellerNTNCNIC: globalData?.ntn_cnic || "",
        sellerBusinessName: globalData?.company_name || globalData?.full_name || "",
        sellerProvince: globalData?.province || "Punjab",
        sellerAddress: globalData?.business_address || globalData?.city || "Lahore",
        buyerNTNCNIC: "1000000000000",
        buyerSalesTaxNo: "",
        buyerBusinessName: "Sandbox Test Buyer",
        buyerProvince: globalData?.province || "Punjab",
        buyerAddress: "Sandbox Test Address",
        buyerRegistrationType: "Unregistered",
        termsOfSale: "Cash",
        invoiceRefNo: "",
        invoiceNumber,
        scenarioId: scenario,
        reason: "",
        items: [{
          hsCode: getHsCode(hsCode),
          productDescription: getHsDescription(hsCode) || "Sandbox sample item",
          rate,
          uoM: getHsUom(hsCode),
          quantity: 1,
          totalValues: 0,
          valueSalesExcludingST,
          fixedNotifiedValueOrRetailPrice: 0,
          salesTaxApplicable,
          salesTaxWithheldAtSource: 0,
          extraTax: ["SN005", "SN028"].includes(String(scenario).toUpperCase()) ? "" : 0,
          furtherTax: 0,
          sroScheduleNo: getHsSroSchedule(hsCode),
          fedPayable: 0,
          discount: 0,
          saleType: getHsSaleType(hsCode) || "Goods at standard rate (default)",
          sroItemSerialNo: getHsSroItem(hsCode),
          validation: "",
          remarks: "",
          invoiceNumber: "",
        }],
        fbrinvoicenumber: "",
        FBRqrcode: "",
        validation: "",
        alreadySubmitted: false,
        disabledReason: "",
        remarks: "",
        expanded: false,
      });
    });

    return invoicesToBuild;
  };

  const flattenInvoicesForExcel = (sourceInvoices) => {
    const rows = [];
    sourceInvoices.forEach((invoice) => {
      (invoice.items || []).forEach((item) => {
        rows.push({
          ...summaryFields.reduce((acc, field) => ({ ...acc, [field]: invoice[field] ?? "" }), {}),
          ...EXCEL_EXPORT_ITEM_FIELDS.reduce((acc, field) => ({ ...acc, [field]: item[field] ?? "" }), {}),
        });
      });
    });
    return rows;
  };

  const handleDownloadSample = () => {
    if (globalData?.is_prod_token) {
      reportUploadError("Sandbox sample is disabled in production mode.", [
        "Switch to sandbox token mode to generate dummy sandbox invoices.",
      ]);
      return;
    }

    const sampleInvoices = buildSandboxSampleInvoices();
    if (!sampleInvoices.length) {
      reportUploadError("No sandbox sample could be generated.", [
        "Assign at least one scenario and one HS code to this user.",
      ]);
      return;
    }

    const rows = flattenInvoicesForExcel(sampleInvoices);

    const excelHeader = toFriendlyExcelHeaders([...summaryFields, ...EXCEL_EXPORT_ITEM_FIELDS]);
    const ws = XLSX.utils.json_to_sheet(toFriendlyExcelRows(rows), { header: excelHeader });
    XLSX.utils.sheet_add_aoa(ws, [excelHeader], { origin: "A1" });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sandbox Sample");
    XLSX.writeFile(wb, "sandbox_sample_invoices.xlsx");
  };

  const handleAutoValidateSandboxSamples = async () => {
    if (globalData?.is_prod_token) {
      reportUploadError("Sandbox auto validation is disabled in production mode.", [
        "Switch to sandbox token mode to use dummy invoice validation.",
      ]);
      return;
    }

    const sampleInvoices = buildSandboxSampleInvoices();
    if (!sampleInvoices.length) {
      reportUploadError("No sandbox sample could be generated.", [
        "Assign at least one scenario and one HS code to this user.",
      ]);
      return;
    }

    let invoicesWithSubmissionStatus = sampleInvoices;
    try {
      setLoader(true);
      invoicesWithSubmissionStatus = await markAlreadySubmittedInvoices(sampleInvoices, false);
    } catch (err) {
      reportUploadError("Could not verify submitted sandbox invoices.", [
        String(err?.message || err) || "Unknown verification error",
      ]);
      return;
    } finally {
      setLoader(false);
    }

    setInvoices(invoicesWithSubmissionStatus);
    setSelected(
      invoicesWithSubmissionStatus
        .map((inv, index) => (inv.alreadySubmitted ? null : index))
        .filter((index) => index !== null)
    );
    setSubmittedUploadSummary("Validating sandbox sample invoices with FBR...");

    const indexes = invoicesWithSubmissionStatus
      .map((inv, index) => (inv.alreadySubmitted ? null : index))
      .filter((index) => index !== null);
    const validatedInvoices = await processInvoiceIndexes(
      invoicesWithSubmissionStatus,
      indexes,
      false,
      "Validating sandbox samples with FBR"
    );

    summarizeValidationResults(validatedInvoices, validatedInvoices.length);
    return validatedInvoices;
  };

  const handleCheckPendingScenarios = () => {
    updateScenarioStatusPanel(invoices);
  };
console.log("newinvoices",invoices)
  /* ------------------ UI ------------------ */
  return (
    <Box
      minHeight="100vh"
      sx={{
        background: "transparent",
        pb: 5,
        maxWidth: 1560,
        mx: "auto",
      }}
    >
      {globalData?.is_expired ? (
        <Typography component="div" sx={{ mb: 2, color: "#b91c1c", fontSize: 14, fontWeight: 800, bgcolor: "#fef2f2", border: "1px solid #fecaca", px: 1.5, py: 1, borderRadius: 2 }}>
          {globalData.message}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "stretch",
          flexWrap: "wrap",
          mb: 2.5,
        }}
      >
        <DashboardCards stats={globalData} />
        <Paper
          elevation={0}
          sx={{
            flex: "1 1 520px",
            p: { xs: 2, md: 2.5 },
            borderRadius: 2,
            border: "1px solid rgba(37, 99, 235, 0.14)",
            background: "linear-gradient(135deg, #0f172a 0%, #1d4ed8 56%, #0f766e 100%)",
            color: "#fff",
            boxShadow: "0 18px 42px rgba(15, 23, 42, 0.18)",
            overflow: "hidden",
            position: "relative",
            "&:before": {
              content: '""',
              position: "absolute",
              right: -40,
              top: -52,
              width: 190,
              height: 190,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.12)",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1, mb: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: "rgba(255,255,255,0.72)", textTransform: "uppercase" }}>
              Invoice command center
            </Typography>
            <Typography sx={{ fontWeight: 950, fontSize: { xs: 22, md: 28 }, lineHeight: 1.15, mt: 0.5 }}>
              Create, validate, and submit FBR invoices faster.
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.76)", mt: 0.75, fontSize: 14, maxWidth: 640 }}>
              Start with a manual invoice, upload Excel data, or run sandbox helpers to prepare the queue.
            </Typography>
          </Box>
          <Box sx={{ position: "relative", zIndex: 1, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="medium"
              startIcon={<AddIcon />}
              disabled={globalData?.is_expired}
              onClick={() => { if (!globalData?.is_expired) setInvoiceOpen(true); }}
              sx={{
                bgcolor: "#22c55e",
                color: "#052e16",
                borderRadius: 1.25,
                fontWeight: 900,
                px: 1.75,
                minHeight: 42,
                boxShadow: "0 12px 24px rgba(34, 197, 94, 0.28)",
                textTransform: "none",
                cursor: globalData?.is_expired ? "not-allowed" : "pointer",
                opacity: globalData?.is_expired ? 0.6 : 1,
                "&:hover": { bgcolor: "#86efac", boxShadow: "0 12px 24px rgba(34, 197, 94, 0.28)" }
              }}
            >
              Add
            </Button>

            <Button
              variant="outlined"
              size="medium"
              startIcon={<UploadFileIcon />}
              disabled={globalData?.is_expired}
              onClick={() => { if (!globalData?.is_expired) fileInput.current.click(); }}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.38)",
                borderRadius: 1.25,
                fontWeight: 900,
                px: 1.5,
                minHeight: 42,
                background: "rgba(255,255,255,0.12)",
                cursor: globalData?.is_expired ? "not-allowed" : "pointer",
                opacity: globalData?.is_expired ? 0.6 : 1,
                textTransform: "none",
                "&:hover": { bgcolor: "rgba(255,255,255,0.18)", borderColor: "rgba(255,255,255,0.6)" },
              }}
            >
              Upload
            </Button>

          <input ref={fileInput} type="file" accept=".xlsx,.xlsm,.xls,.pdf,application/pdf" hidden onChange={handleFile} />

            <Button
              variant="outlined"
              size="medium"
              startIcon={<DownloadIcon />}
              sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.38)", borderRadius: 1.25, fontWeight: 900, px: 1.5, minHeight: 42, background: "rgba(255,255,255,0.12)", textTransform: "none", "&:hover": { bgcolor: "rgba(255,255,255,0.18)", borderColor: "rgba(255,255,255,0.6)" } }}
              onClick={handleDownloadSample}
            >
              Sample
            </Button>

            <Button
              variant="outlined"
              size="medium"
              disabled={Boolean(globalData?.is_prod_token) || globalData?.is_expired || progress.total > 0}
              startIcon={<CheckCircleIcon />}
              sx={{ color: "#0f172a", borderColor: "#bae6fd", borderRadius: 1.25, fontWeight: 900, px: 1.5, minHeight: 42, background: "#e0f2fe", textTransform: "none", "&:hover": { bgcolor: "#bae6fd", borderColor: "#7dd3fc" } }}
              onClick={handleAutoValidateSandboxSamples}
            >
              Auto Validate
            </Button>

            <Button
              variant="contained"
              size="medium"
              disabled={Boolean(globalData?.is_prod_token) || globalData?.is_expired || progress.total > 0}
              sx={{ bgcolor: "#ffffff", color: "#0f172a", borderRadius: 1.25, fontWeight: 900, px: 1.75, minHeight: 42, boxShadow: "none", textTransform: "none", "&:hover": { bgcolor: "#f8fafc", boxShadow: "none" } }}
              onClick={handleCheckPendingScenarios}
            >
              Pending
            </Button>
          </Box>
        </Paper>
      </Box>

      <InvoiceForm open={invoiceOpen} onClose={() => setInvoiceOpen(false)} onSave={handleSaveInvoice} />

      {progress.total > 0 && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "#eff6ff", border: "1px solid #bfdbfe" }}>
          <Typography fontWeight={700}>
            {progress.label || "Processing invoices"}: {progress.sent} of {progress.total}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Processing {FBR_BATCH_SIZE} invoice at a time. Any request taking more than {Math.round(FBR_REQUEST_TIMEOUT_MS / 1000)} seconds is marked as FBR API timeout.
          </Typography>
          <LinearProgress variant="determinate" value={Math.round((progress.sent / progress.total) * 100)} sx={{ my: 1 }} />
        </Paper>
      )}

	      {submittedUploadSummary && (
	        <Alert severity={pendingValidationCount > 0 ? "warning" : "success"} sx={{ mb: 2, borderRadius: 2 }}>
	          {submittedUploadSummary}
	        </Alert>
	      )}

	      {approvalRows.length > 0 && (
	        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, background: "#ffffff", border: "1px solid #e5e7eb", boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)", mb: 2 }}>
	          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5} sx={{ flexWrap: "wrap", mb: 1.5 }}>
	            <Box>
	              <Typography sx={{ fontWeight: 900, color: "#111827", fontSize: 18 }}>
	                {isAdminUser ? "Pending Invoices" : "Approval Status"}
	              </Typography>
	              <Typography sx={{ color: "#6b7280", fontSize: 13 }}>
	                {approvalRows.length} invoice(s) {isAdminUser ? "waiting for admin action." : "in approval workflow."}
	              </Typography>
	            </Box>
	            <Button variant="outlined" disabled={approvalLoading} onClick={loadInvoiceApprovals} sx={{ textTransform: "none", fontWeight: 800 }}>
	              Refresh
	            </Button>
	          </Box>
	          <Box sx={{ overflow: "auto" }}>
	            <Table size="small">
	              <TableHead sx={{ "& th": { bgcolor: "#f9fafb", color: "#374151", fontWeight: 900, whiteSpace: "nowrap", borderBottom: "1px solid #e5e7eb" } }}>
	                <TableRow>
	                  <TableCell>Invoice Number</TableCell>
	                  <TableCell>Date</TableCell>
	                  <TableCell>Buyer</TableCell>
	                  <TableCell>Created By</TableCell>
	                  <TableCell>Status</TableCell>
	                  <TableCell>Remarks</TableCell>
	                  {isAdminUser && <TableCell>Actions</TableCell>}
	                </TableRow>
	              </TableHead>
	              <TableBody>
	                {approvalRows.map((row) => (
	                  <TableRow key={row.id} sx={{ "& td": { whiteSpace: "nowrap" } }}>
	                    <TableCell>{row.invoice_number || row.invoice_payload?.invoiceNumber || ""}</TableCell>
	                    <TableCell>{row.invoice_date || row.invoice_payload?.invoiceDate || ""}</TableCell>
	                    <TableCell>{row.buyer_business_name || row.invoice_payload?.buyerBusinessName || ""}</TableCell>
	                    <TableCell>{row.created_by_name || row.created_by_email || ""}</TableCell>
	                    <TableCell>
	                      <Box component="span" sx={{
	                        px: 1,
	                        py: 0.35,
	                        borderRadius: 999,
	                        fontSize: 12,
	                        fontWeight: 900,
	                        bgcolor: row.status === "cancelled" ? "#fee2e2" : "#fef3c7",
	                        color: row.status === "cancelled" ? "#991b1b" : "#92400e",
	                      }}>
	                        {row.status}
	                      </Box>
	                    </TableCell>
	                    <TableCell>{row.cancel_remarks || ""}</TableCell>
	                    {isAdminUser && (
	                      <TableCell>
	                        <Button size="small" variant="contained" color="success" disabled={progress.total > 0} onClick={() => handleSubmitApproval(row)} sx={{ mr: 1, textTransform: "none", fontWeight: 800 }}>
	                          Submit
	                        </Button>
	                        <Button size="small" variant="outlined" color="error" onClick={() => openCancelApprovalDialog(row)} sx={{ textTransform: "none", fontWeight: 800 }}>
	                          Cancel
	                        </Button>
	                      </TableCell>
	                    )}
	                  </TableRow>
	                ))}
	              </TableBody>
	            </Table>
	          </Box>
	        </Paper>
	      )}
	
	      {scenarioCheckRows.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, background: "#ffffff", border: "1px solid #e5e7eb", boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)", mb: 2 }}>
          <Typography fontWeight={900} sx={{ color: "#111827", mb: 0.5 }}>
            Sandbox Scenario Status
          </Typography>
          {scenarioCheckSummary && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {scenarioCheckSummary}
            </Typography>
          )}
          <Table size="small">
            <TableHead sx={{ "& th": { bgcolor: "#f9fafb", color: "#374151", fontWeight: 900, borderBottom: "1px solid #e5e7eb" } }}>
              <TableRow>
                <TableCell>Scenario ID</TableCell>
                <TableCell>HS Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>FBR Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scenarioCheckRows.map((row) => (
                <TableRow key={row.scenarioId} sx={{ background: row.status === "Completed" ? "#f0fdf4" : "#fff7ed" }}>
                  <TableCell>{row.scenarioId}</TableCell>
                  <TableCell>{row.hsCode}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.remarks}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {invoices?.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: 2,
            border: "1px solid rgba(148, 163, 184, 0.22)",
            background: "#ffffff",
            boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} gap={2}>
            <Box>
              <Typography sx={{ color: "#0f172a", fontWeight: 950, fontSize: { xs: 20, md: 24 }, lineHeight: 1.2 }}>
              Your invoice queue is empty.
            </Typography>
              <Typography sx={{ color: "#64748b", mt: 0.6, fontSize: 14, maxWidth: 640 }}>
              Add an invoice manually or upload an Excel/PDF file to start validation and FBR submission.
            </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={globalData?.is_expired}
                onClick={() => { if (!globalData?.is_expired) setInvoiceOpen(true); }}
                sx={{ bgcolor: "#2563eb", fontWeight: 900, borderRadius: 1.5, boxShadow: "0 12px 24px rgba(37, 99, 235, 0.24)", textTransform: "none", "&:hover": { bgcolor: "#1d4ed8" } }}
              >
                Create Invoice
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                disabled={globalData?.is_expired}
                onClick={() => { if (!globalData?.is_expired) fileInput.current.click(); }}
                sx={{ borderColor: "#cbd5e1", color: "#334155", fontWeight: 900, borderRadius: 1.5, textTransform: "none", "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" } }}
              >
                Upload Excel/PDF
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}

      {invoices?.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, background: "#ffffff", border: "1px solid #e5e7eb", boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)", mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1.5} sx={{ flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontWeight: 900, color: "#111827", fontSize: 18 }}>
                Invoice Queue
              </Typography>
              <Typography sx={{ color: "#6b7280", fontSize: 13 }}>
                {invoices.length} invoice(s) loaded. {pendingFbrSubmitCount} ready for FBR, {pendingValidationCount} need validation.
                {selected.length ? ` Selected: ${selected.length}.` : ""}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
              {invoices.some(inv => inv.fbrinvoicenumber) && (
                <Button variant="outlined" color="primary" onClick={handleDownloadExcel}
                  sx={{ borderColor: "#d1d5db", color: "#374151", fontWeight: 800, borderRadius: 1.5, textTransform: "none" }}>
                  Download Excel
                </Button>
              )}
              {renderInvoiceQueueActions()}
            </Box>
          </Box>
          <Box sx={{ height: { xs: "60vh", md: "calc(100vh - 360px)" }, minHeight: 360, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead sx={{ "& th": { bgcolor: "#f9fafb", color: "#374151", fontWeight: 900, whiteSpace: "nowrap", borderBottom: "1px solid #e5e7eb" } }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <div style={{display:"flex"}}>
                      <Checkbox
                        indeterminate={selected.length > 0 && selected.length < selectableInvoiceIndexes.length}
                        checked={selectableInvoiceIndexes.length > 0 && selected.length === selectableInvoiceIndexes.length}
                        disabled={selectableInvoiceIndexes.length === 0}
                        onChange={handleSelectAll}
                      />
                      {selectableInvoiceIndexes.length > 0 && selected.length === selectableInvoiceIndexes.length &&
                        <TableToolbar
                          numSelected={selected.length}
                          handleDeleteAll={() => {
                            setInvoices((prev) => prev.filter((_, idx) => !selected.includes(idx)));
                            setSelected([]);
                          }}
                        />
                      }
                    </div>
                  </TableCell>
                  <TableCell />
                  <TableCell>Invoice Number</TableCell>
                  <TableCell>Scenerio Id</TableCell>

                  <TableCell>Buyer</TableCell>
                  <TableCell>Buyer Sales Tax No</TableCell>
                  <TableCell>Terms of Sale</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>FBR Invoice</TableCell>
                  <TableCell>FBR QR Code</TableCell>
                  <TableCell>Validation</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((inv, idx) => {
                  const invoiceHasError = hasInvoiceError(inv);

                  return (
                  <React.Fragment key={idx}>
                    <TableRow
                      sx={{
                        background: inv.alreadySubmitted ? "#f3f4f6" : invoiceHasError ? "#fee2e2" : "#fff",
                        "&.Mui-selected, &.Mui-selected:hover": {
                          background: invoiceHasError ? "#fecaca" : "#eef2ff",
                        },
                        "&:hover": {
                          background: inv.alreadySubmitted ? "#f3f4f6" : invoiceHasError ? "#fecaca" : "#f9fafb",
                        },
                        "& td": { borderBottom: "1px solid #eef2f7", whiteSpace: "nowrap" },
                        "& td:first-of-type": {
                          borderLeft: invoiceHasError ? "4px solid #dc2626" : "4px solid transparent",
                        },
                      }}
                      selected={selected.includes(idx)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.includes(idx)}
                          disabled={inv.alreadySubmitted}
                          onChange={() => handleSelect(idx)}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleExpand(idx)} size="small">
                          {inv.expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{inv.invoiceNumber}</TableCell>
                                            <TableCell>{inv.scenarioId}</TableCell>

                      <TableCell>{inv.buyerBusinessName}</TableCell>
                      <TableCell>{inv.buyerSalesTaxNo || ""}</TableCell>
                      <TableCell>{inv.termsOfSale || ""}</TableCell>
                      <TableCell>{inv.invoiceDate ? inv.invoiceDate : ""}</TableCell>
                      <TableCell>{inv.items.length}</TableCell>
                      <TableCell>{inv.fbrinvoicenumber || ""}</TableCell>
                      <TableCell>
                        {inv.FBRqrcode
                          ? <QRCodeCanvas value={inv.FBRqrcode} size={80} bgColor="#ffffff" fgColor="#000000" level="H" includeMargin />
                          : inv.FBRqrcode}
                      </TableCell>
                      <TableCell>
                        {inv.validation ? (
                          <Box
                            component="span"
                            sx={{
                              px: 1,
                              py: 0.35,
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 900,
                              bgcolor: inv.validation === "Valid" ? "#dcfce7" : "#fee2e2",
                              color: inv.validation === "Valid" ? "#166534" : "#991b1b",
                            }}
                          >
                            {inv.validation}
                          </Box>
                        ) : ""}
                      </TableCell>
                      <TableCell>{inv.disabledReason || inv.remarks || ""}</TableCell>
                      <TableCell>
                        {inv.validation === "Valid" && (
                          <IconButton onClick={() => setDashboardPrintChoiceInvoice(inv)} color="primary">
                            <PrintIcon />
                          </IconButton>
                        )}
                        <IconButton disabled={inv.alreadySubmitted} onClick={() => handleEdit(idx)}><EditIcon /></IconButton>
                        <IconButton disabled={inv.alreadySubmitted} onClick={() => handleDelete(idx)}><DeleteIcon /></IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={14} sx={{ p: 0, border: 0 }}>
                        <Collapse in={inv.expanded}>
                          <Box sx={{ m: 2 }}>
                            <Typography fontWeight={600}>Items</Typography>
                            <Table size="small">
                              <TableHead sx={{ "& th": { bgcolor: "#f9fafb", fontWeight: 900, color: "#374151", whiteSpace: "nowrap" } }}>
                                <TableRow>
                                  {itemFields.map(f => (<TableCell key={f}>{f}</TableCell>))}
                                  <TableCell>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {inv.items.map((item, itemIdx) => {
                                  const itemHasError = hasItemError(item);

                                  return (
                                  <TableRow
                                    key={itemIdx}
                                    sx={{
                                      background: itemHasError ? "#fee2e2" : "#fff",
                                      "&:hover": { background: itemHasError ? "#fecaca" : "#f9fafb" },
                                      "& td:first-of-type": {
                                        borderLeft: itemHasError ? "4px solid #dc2626" : "4px solid transparent",
                                      },
                                    }}
                                  >
                                    {itemFields.map(f => (<TableCell key={f}>{item[f]}</TableCell>))}
                                    <TableCell>
                                      <IconButton size="small" disabled={inv.alreadySubmitted} onClick={() => {
                                        setEditItem({ ...item });
                                        setEditItemIdx(itemIdx);
                                        setEditInvoiceIdx(idx);
                                        setEditItemDialogOpen(true);
                                      }}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton size="small" disabled={inv.alreadySubmitted} onClick={() => {
                                        setInvoices(prev => {
                                          const updated = [...prev];
                                          updated[idx].items = updated[idx].items.filter((_, i) => i !== itemIdx);
                                          return updated;
                                        });
                                      }}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} gap={2} sx={{ flexWrap: "wrap" }}>
            <Typography sx={{ color: "#4b5563", fontWeight: 600 }}>
              {invoices.length} invoice(s) loaded. {pendingFbrSubmitCount} ready for FBR, {pendingValidationCount} need validation.
              {selected.length ? ` Selected: ${selected.length}.` : ""}
            </Typography>
            {renderInvoiceQueueActions()}
          </Box>
        </Paper>
      )}

      <Dialog
        open={!!dashboardPrintChoiceInvoice}
        onClose={() => setDashboardPrintChoiceInvoice(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: "#111827" }}>
          Print Invoice
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#64748b", fontSize: 14 }}>
            Choose how you want this invoice to be printed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap" }}>
          <Button
            onClick={() => setDashboardPrintChoiceInvoice(null)}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => handlePrintValidatedInvoice(dashboardPrintChoiceInvoice, "normal")}
            sx={{ textTransform: "none", fontWeight: 900, borderRadius: 1.5 }}
          >
            Current Invoice
          </Button>
          <Button
            variant="outlined"
            onClick={() => handlePrintValidatedInvoice(dashboardPrintChoiceInvoice, "modern")}
            sx={{ textTransform: "none", fontWeight: 900, borderRadius: 1.5 }}
          >
            New Format
          </Button>
          <Button
            variant="outlined"
            onClick={() => handlePrintValidatedInvoice(dashboardPrintChoiceInvoice, "logo")}
            sx={{ textTransform: "none", fontWeight: 900, borderRadius: 1.5 }}
          >
            With Logo
          </Button>
          <Button
            variant="contained"
            onClick={() => handlePrintValidatedInvoice(dashboardPrintChoiceInvoice, "preprinted")}
            sx={{ textTransform: "none", fontWeight: 900, borderRadius: 1.5 }}
          >
            Print on Letter Head
          </Button>
        </DialogActions>
      </Dialog>

	      <div
	        id="dashboard-printable-content"
        ref={dashboardPrintRef}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          zIndex: -1,
          visibility: "hidden",
        }}
	        dangerouslySetInnerHTML={{ __html: dashboardPrintHtml }}
	      />

	      <Dialog open={Boolean(cancelApprovalRow)} onClose={() => setCancelApprovalRow(null)} maxWidth="sm" fullWidth>
	        <DialogTitle sx={{ fontWeight: 900 }}>Cancel Pending Invoice</DialogTitle>
	        <DialogContent>
	          <Typography sx={{ color: "#64748b", fontSize: 14, mb: 2 }}>
	            Add remarks for the non-admin user before sending this invoice back.
	          </Typography>
	          <TextField
	            label="Remarks"
	            value={cancelRemarks}
	            onChange={(e) => setCancelRemarks(e.target.value)}
	            multiline
	            minRows={3}
	            fullWidth
	            required
	          />
	        </DialogContent>
	        <DialogActions sx={{ px: 3, pb: 2 }}>
	          <Button onClick={() => setCancelApprovalRow(null)} sx={{ textTransform: "none", fontWeight: 800 }}>
	            Close
	          </Button>
	          <Button variant="contained" color="error" onClick={handleCancelApprovalSubmit} sx={{ textTransform: "none", fontWeight: 900 }}>
	            Cancel Invoice
	          </Button>
	        </DialogActions>
	      </Dialog>

	      {/* Edit Invoice Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Edit Invoice</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            {Object.keys(editInvoice).length > 0 && (
              <Box>
                <Typography sx={{ fontWeight: 900, color: "#0f172a", mb: 1 }}>Summary</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.5 }}>
                  {summaryFields.slice(0, 5).map((col) => {
                    const options = getSummaryFieldOptions(col);
                    return (
                      <TextField
                        key={col}
                        select={options.length > 0}
                        label={getFieldLabel(col)}
                        fullWidth
                        type={col === "invoiceDate" ? "date" : "text"}
                        value={editInvoice[col] || ""}
                        InputLabelProps={col === "invoiceDate" ? { shrink: true } : {}}
                        onChange={(e) => setEditInvoice((prev) => ({ ...prev, [col]: e.target.value }))}
                      >
                        {options.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    );
                  })}
                </Box>

                <Typography sx={{ fontWeight: 900, color: "#0f172a", mt: 2.5, mb: 1 }}>Seller</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5 }}>
                  {["sellerNTNCNIC", "sellerBusinessName", "sellerProvince", "sellerAddress"].map((col) => {
                    const options = getSummaryFieldOptions(col);
                    return (
                      <TextField
                        key={col}
                        select={options.length > 0}
                        label={getFieldLabel(col)}
                        fullWidth
                        value={editInvoice[col] || ""}
                        onChange={(e) => setEditInvoice((prev) => ({ ...prev, [col]: e.target.value }))}
                      >
                        {options.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    );
                  })}
                </Box>

                <Typography sx={{ fontWeight: 900, color: "#0f172a", mt: 2.5, mb: 1 }}>Buyer</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5 }}>
                  {["buyerNTNCNIC", "buyerSalesTaxNo", "buyerBusinessName", "buyerProvince", "buyerAddress", "buyerRegistrationType", "termsOfSale"].map((col) => {
                    const options = getSummaryFieldOptions(col);
                    return (
                      <TextField
                        key={col}
                        select={options.length > 0}
                        label={getFieldLabel(col)}
                        fullWidth
                        value={editInvoice[col] || ""}
                        onChange={(e) => setEditInvoice((prev) => ({ ...prev, [col]: e.target.value }))}
                      >
                        {options.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="secondary">Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={editItemDialogOpen} onClose={() => setEditItemDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Edit Item</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.5 }}>
            {itemFields.filter((field) => !["validation", "remarks", "invoiceNumber"].includes(field)).map((field) => {
              const options = getItemFieldOptions(field);
              const isNumericField = NUMERIC_FIELDS.has(field) || field === "quantity";
              return (
                <TextField
                  key={field}
                  select={options.length > 0}
                  label={getFieldLabel(field)}
                  fullWidth
                  type={!options.length && isNumericField ? "number" : "text"}
                  value={editItem[field] || ""}
                  onChange={(e) => {
                    if (field === "hsCode") {
                      applyHsCodeToEditItem(e.target.value);
                    } else {
                      setEditItem((prev) => ({ ...prev, [field]: e.target.value }));
                    }
                  }}
                >
                  {options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItemDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setInvoices(prev => {
                const updated = [...prev];
                updated[editInvoiceIdx].items[editItemIdx] = { ...editItem };
                return updated;
              });
              setEditItemDialogOpen(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Result / Error Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Result</DialogTitle>
        <DialogContent>
          <Typography style={{ whiteSpace: "pre-line" }}>{dialogContent}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={approvalDialogOpen}
        onClose={handleCancelApproval}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: "linear-gradient(180deg, #ffffff 0%, #f7f5ff 100%)",
            boxShadow: "0 18px 60px rgba(76, 58, 160, 0.18)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
          <Typography variant="h5" fontWeight={800} sx={{ color: "#312a66" }}>
            Admin Approval
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Typography sx={{ color: "#625f7a", fontSize: 16, lineHeight: 1.6, mb: 3 }}>
            This invoice date does not belong to the current allowed month range. Enter the 6-digit admin code to continue with FBR submission.
          </Typography>

          <Typography sx={{ color: "#6c5ad7", fontWeight: 700, mb: 1.5, fontSize: 14, letterSpacing: 0.3 }}>
            Enter Admin Code
          </Typography>

          <Box
            onPaste={handleApprovalPaste}
            sx={{
              display: "flex",
              gap: 1.25,
              justifyContent: "space-between",
              mb: approvalError ? 1 : 2,
            }}
          >
            {Array.from({ length: 6 }).map((_, index) => (
              <Box
                key={index}
                component="input"
                ref={(el) => {
                  approvalInputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={approvalCode[index] && approvalCode[index] !== " " ? approvalCode[index] : ""}
                onChange={(e) => handleApprovalDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleApprovalKeyDown(index, e)}
                sx={{
                  width: 52,
                  height: 58,
                  borderRadius: 2.5,
                  border: approvalError ? "2px solid #dc2626" : "2px solid #d9d2fb",
                  background: "#fff",
                  textAlign: "center",
                  fontSize: 24,
                  fontWeight: 800,
                  color: "#312a66",
                  outline: "none",
                  boxShadow: "inset 0 1px 4px rgba(108, 90, 215, 0.08)",
                  transition: "all .2s ease",
                  "&:focus": {
                    borderColor: "#6c5ad7",
                    boxShadow: "0 0 0 4px rgba(108, 90, 215, 0.14)",
                  },
                }}
              />
            ))}
          </Box>

          {approvalError && (
            <Typography sx={{ color: "#dc2626", fontSize: 13, fontWeight: 600 }}>
              {approvalError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: "space-between" }}>
          <Button
            onClick={handleCancelApproval}
            sx={{
              color: "#8c2fd0",
              fontWeight: 700,
              px: 0,
              "&:hover": { background: "transparent", color: "#6c5ad7" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVerifyApprovalCode}
            variant="contained"
            disabled={approvalCode.replace(/\s/g, "").length !== 6 || isVerifying}
            sx={{
              minWidth: 180,
              borderRadius: 3,
              fontWeight: 800,
              background: "linear-gradient(90deg, #6c5ad7 0%, #4f3bb4 100%)",
              boxShadow: "0 10px 24px rgba(108, 90, 215, 0.28)",
              "&:hover": {
                background: "linear-gradient(90deg, #5d4ac7 0%, #46359f 100%)",
              },
              "&.Mui-disabled": {
                background: "#dad7e8",
                color: "#9b97aa",
              },
            }}
          >
            {isVerifying ? "Verifying..." : "Verify & Proceed"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
